import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, idr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useDebounce } from "../../hooks/useDebounce";
import { useModalGuard, UnsavedGuardBar } from "../../hooks/useModalGuard";
import { useCabangOptions } from "../../lib/cabang";
import { DatePicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta } from "../../components/ui/responsive-table";
import { hariIniISO, formatTanggal, dateDariIso } from "../../lib/tanggal";
import InputRupiah from "../../components/InputRupiah";
import AttachmentPanel from "./AttachmentPanel";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";
import { BatasDaftarNotice } from "./_shared";
import {
  Plus, Percent, Search, RotateCcw, X, ChevronRight, ListFilter, LayoutDashboard,
  AlertTriangle, CheckCircle2, Clock, Send, Ban, Wallet, Trash2, Pencil,
  Lock, LockOpen, Upload, FileDown, FileSpreadsheet, Paperclip, PlusCircle, CalendarRange, PackageSearch,
  ArrowRightLeft, History, Undo2,
} from "lucide-react";

// `GET /cashback/programs` mengurut created_at desc lalu memotong
// `to_list(1000)` di backend TANPA parameter urut dari klien (lihat
// cashback_claim.py) — sort di tabel ini murni client-side.
const CB_PROGRAM_BATAS_SERVER = 1000;

// KENAPA path literal terpisah: router `cashback_claim.py` di backend memakai
// path literal ("/cashback/programs", dst) dan dipasang `prefix="/api"` SAJA —
// pola SAMA dengan `credit_note.py` (lihat `CN_BASE` di FinanceCreditNote.jsx),
// BUKAN di bawah agregator "/finance". Kalau prefix di server.py diubah, cukup
// ubah SATU baris ini.
export const CB_BASE = "/cashback";

// ============ Jenis, kanal, status program — SATU sumber di layar ============
// Pasangan konstanta `backend/routers/cashback_mesin.py` (brief owner 22 Sep 2026,
// bagian A). Nilai-nilai ini dikirim ke API dan dipakai menyaring baris, jadi
// kalau backend menambah status, tambahkan DI SINI saja — jebakan "status baru
// mematikan penjaga yang menyaring literal" (CLAUDE.md) juga berlaku di layar.
export const JENIS_AKTIVASI = "aktivasi";
export const JENIS_CASHBACK = "cashback";
export const KANAL_TOKO = "toko";
export const KANAL_DEALER = "dealer";
export const ST_PROGRAM_AKTIF = "aktif";
export const ST_PROGRAM_MENUNGGU = "menunggu_selesai";
export const ST_PROGRAM_SELESAI = "selesai";
// Masih dikejar (pengingat deadline & hitungan hangus) — `STATUS_PROGRAM_BERJALAN`.
const ST_PROGRAM_BERJALAN = [ST_PROGRAM_AKTIF, ST_PROGRAM_MENUNGGU];
// Brief A.1/A.2: yang boleh dipindah hanya "produk yang Tidak Eligible & Menunggu
// Aktivasi" — sama dengan `cashback_mesin.ELIG_BOLEH_PINDAH`.
const ELIG_BOLEH_PINDAH = ["not_eligible", "pending_activation"];
const ELIG_BELUM_TERJUAL = "belum_terjual";

export const RUTE_DASHBOARD_PP = "/finance/price-protection/dashboard";
// Program LAMA (tanpa jenis/kanal) tidak punya salah satu dari empat halaman —
// tinggal di tab "Program Lama" dashboard. Sama dengan `cashback_mesin.RUTE_PROGRAM_LAMA`.
export const RUTE_PROGRAM_LAMA = `${RUTE_DASHBOARD_PP}?view=lama`;
export const ruteHalamanPP = (jenis, kanal) => `/finance/price-protection/${jenis}/${kanal}`;

export function labelJenis(jenis) {
  return jenis === JENIS_AKTIVASI ? "Support Aktivasi" : "Support Cashback";
}

/** Nama toko sendiri datang dari Branding (`useAuth().branding.nama_toko_sendiri`,
 *  keputusan A1) — EppleHub dijual white-label, jadi tidak ada nama toko yang
 *  boleh tertanam di kode. */
export function labelKanal(kanal, namaToko) {
  if (kanal === KANAL_DEALER) return "Dealer Partner";
  if (kanal === KANAL_TOKO) return namaToko || "EppleHub";
  return "Semua Kanal (program lama)";
}

/** Tautan yang langsung membuka jendela detail satu program. `rute` datang dari
 *  backend (`_tampilkan_program.rute` / Pusat Approval `modul`) dan bisa sudah
 *  membawa query string (`?view=lama`). */
export function tautanProgram(rute, id) {
  const dasar = rute || RUTE_DASHBOARD_PP;
  return `${dasar}${dasar.includes("?") ? "&" : "?"}program=${encodeURIComponent(id)}`;
}

// ============ Helper unduh berkas biner (template Excel, dst) ============
// Pola SAMA dengan `_downloadBlob` di FinanceLaporan.jsx — diduplikasi kecil
// di sini (bukan diimpor lintas halaman) karena keduanya berdiri sendiri;
// diekspor supaya CashbackClaim.jsx (ekspor xlsx/pdf) memakai SATU sumber
// yang sama dalam keluarga berkas cashback ini.
export async function downloadBlob(url, filename, mime, params) {
  const res = await api.get(url, { responseType: "blob", params });
  const blob = new Blob([res.data], { type: mime });
  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(objUrl);
}
const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// ============ Status & pill bersama (dipakai CashbackClaim.jsx + CashbackClaimDetail.jsx) ============
const PROGRAM_STATUS_META = {
  [ST_PROGRAM_AKTIF]:    { label: "Aktif",   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  [ST_PROGRAM_MENUNGGU]: { label: "Menunggu Approval Selesai", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  [ST_PROGRAM_SELESAI]:  { label: "Selesai", cls: "bg-slate-800 text-white border-slate-800" },
  // 'ditutup' lama sudah dinormalisasi backend jadi 'selesai' (`_tampilkan_program`);
  // entri ini hanya jaring pengaman kalau ada respons yang belum lewat sana.
  ditutup:               { label: "Selesai", cls: "bg-slate-800 text-white border-slate-800" },
};
export function ProgramStatusPill({ status }) {
  const m = PROGRAM_STATUS_META[status] || PROGRAM_STATUS_META[ST_PROGRAM_AKTIF];
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

export const CLAIM_STATUS_META = {
  draft:               { label: "Draft",               cls: "bg-slate-100 text-slate-600 border-slate-200" },
  submitted:           { label: "Disubmit ke Supplier", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  diputuskan:          { label: "Diputuskan",           cls: "bg-amber-50 text-amber-700 border-amber-200" },
  settlement_sebagian: { label: "Settlement Sebagian",  cls: "bg-orange-50 text-orange-700 border-orange-200" },
  settled:             { label: "Settled (Lunas)",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  closed:              { label: "Closed",               cls: "bg-slate-800 text-white border-slate-800" },
  dibatalkan:          { label: "Dibatalkan",           cls: "bg-rose-50 text-rose-700 border-rose-200" },
};
export function ClaimStatusPill({ status }) {
  const m = CLAIM_STATUS_META[status] || CLAIM_STATUS_META.draft;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const ITEM_STATUS_META = {
  draft:       { label: "Draft",      cls: "bg-slate-100 text-slate-600 border-slate-200" },
  submitted:   { label: "Disubmit",   cls: "bg-sky-50 text-sky-700 border-sky-200" },
  approved:    { label: "Disetujui",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected:    { label: "Ditolak",    cls: "bg-rose-50 text-rose-700 border-rose-200" },
  received:    { label: "Diterima",  cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  dibatalkan:  { label: "Dibatalkan", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
export function ItemStatusPill({ status }) {
  const m = ITEM_STATUS_META[status] || ITEM_STATUS_META.draft;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

const ELIGIBILITY_META = {
  eligible:           { label: "Eligible",         cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pending_activation: { label: "Menunggu Aktivasi", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  not_eligible:       { label: "Tidak Eligible",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  already_claimed:    { label: "Sudah Diklaim",    cls: "bg-sky-50 text-sky-700 border-sky-200" },
  // Keputusan A4: unit yang masih di stok tampil di program Cashback, hanya dibaca.
  [ELIG_BELUM_TERJUAL]: { label: "Belum Terjual",  cls: "bg-violet-50 text-violet-700 border-violet-200" },
};
export function EligibilityPill({ status }) {
  const m = ELIGIBILITY_META[status] || ELIGIBILITY_META.not_eligible;
  return <span className={`inline-block text-[11px] px-2 py-0.5 rounded border font-medium whitespace-nowrap ${m.cls}`}>{m.label}</span>;
}

/** Penanda urgensi deadline submit (brief butir 23) — murni perbandingan
 *  TANGGAL kalender (bukan penghitungan uang), jadi aman dihitung di klien.
 *  `submittedAfterDeadline` datang APA ADANYA dari backend (`batch.
 *  submitted_after_deadline`), tidak pernah ditebak ulang di sini. */
export function DeadlineBadge({ deadline, statusBatch, submittedAfterDeadline }) {
  if (!deadline) return null;
  if (submittedAfterDeadline) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
        <AlertTriangle className="w-3 h-3" /> Disubmit lewat deadline
      </span>
    );
  }
  if (statusBatch && statusBatch !== "draft") {
    return <span className="text-[11px] text-slate-500">Deadline {formatTanggal(deadline)}</span>;
  }
  const hariIni = dateDariIso(hariIniISO());
  const batas = dateDariIso(deadline);
  if (!hariIni || !batas) return null;
  const hariSisa = Math.round((batas - hariIni) / 86400000);
  if (hariSisa < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
        <AlertTriangle className="w-3 h-3" /> OVERDUE {Math.abs(hariSisa)} hari — berisiko hangus
      </span>
    );
  }
  if (hariSisa <= 3) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-amber-100 text-amber-800 border-amber-300 whitespace-nowrap">
        <Clock className="w-3 h-3" /> H-{hariSisa} ({formatTanggal(deadline)})
      </span>
    );
  }
  return <span className="text-[11px] text-slate-500">Deadline {formatTanggal(deadline)}</span>;
}

/** Penanda "klaim dibuat di luar periode klaim program" (permintaan bos 21 Agu
 *  E1: JANGAN ditolak — beri peringatan merah, biarkan tersimpan). Pola SAMA
 *  dengan `DeadlineBadge` di atas: `diLuarPeriodeKlaim` datang APA ADANYA dari
 *  backend (`batch.di_luar_periode_klaim`), tidak pernah ditebak ulang di
 *  sini — beda gerbang dari periode AKTIVASI (yang tetap menolak 400). */
export function PeriodeKlaimBadge({ diLuarPeriodeKlaim, alasan }) {
  if (!diLuarPeriodeKlaim) return null;
  return (
    <span title={alasan || ""}
          className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border font-medium bg-rose-600 text-white border-rose-600 whitespace-nowrap">
      <AlertTriangle className="w-3 h-3" /> Di luar periode klaim
    </span>
  );
}

// ============ Modal & Field generik (pola sama dengan FinanceCreditNote.jsx) ============
export const inputCls = "w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200";

export function Field({ label, hint, children, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

// `dangerous` opsional (default false = tutup langsung, perilaku sama seperti
// sebelumnya) — pasang HANYA pada modal yang formnya panjang atau menyentuh
// nominal yang diketik staf sendiri. Lihat docstring useModalGuard.jsx untuk
// alasan lengkap; di berkas ini yang ditandai cuma Program Price Protection
// (daftar SKU + harga lama/baru, lihat titik panggilnya di bawah).
export function Modal({ title, icon: Icon, tone, children, onClose, onSubmit, submitLabel, busy, disabled, testid, wide, dangerous }) {
  const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={guard.requestClose}>
      <div ref={guard.contentRef} onClick={(e) => e.stopPropagation()} data-testid={testid}
           className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[88vh] flex flex-col shadow-2xl`}>
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${tone === "rose" ? "text-rose-600" : "text-emerald-600"}`} />} {title}
          </h3>
          <button onClick={guard.requestClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        {guard.confirming && (
          <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid={testid ? `${testid}-guard` : "modal-guard-bar"} />
        )}
        {onSubmit && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
            <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">Batal</button>
            <button onClick={onSubmit} disabled={busy || disabled} data-testid={`${testid}-submit`}
                    className={`h-10 px-4 rounded-lg text-white text-sm font-medium disabled:opacity-50 ${tone === "rose" ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {busy ? "Memproses..." : submitLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReasonModal({ title, icon, label, submitLabel, onClose, onSubmit, testid, children }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    setBusy(true);
    try { await onSubmit(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} icon={icon} tone="rose" onClose={onClose} onSubmit={submit} submitLabel={submitLabel} busy={busy} disabled={!reason.trim()} testid={testid}>
      {children}
      <Field label={label}>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`${testid}-input`} className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}

/** Konfirmasi dengan catatan OPSIONAL (ajukan/setujui Selesai) — beda dari
 *  ReasonModal yang mewajibkan alasan (tolak / buka kembali). */
function KonfirmasiCatatanModal({ title, icon, submitLabel, onClose, onSubmit, testid, children }) {
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(catatan.trim()); } finally { setBusy(false); }
  };
  return (
    <Modal title={title} icon={icon} onClose={onClose} onSubmit={submit} submitLabel={submitLabel} busy={busy} testid={testid}>
      {children}
      <Field label="Catatan (opsional)">
        <textarea rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid={`${testid}-catatan`} className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}

export function ActionBtn({ icon: Icon, label, onClick, testid, primary, tone, disabled, title }) {
  const cls = tone === "rose" ? "border-rose-200 text-rose-700 hover:bg-rose-50"
    : tone === "amber" ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600"
    : primary ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700"
    : "border-slate-200 text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title}
            className={`h-9 px-3 rounded-lg border text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50 ${cls}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }

// ============ Program yang terbuka dicatat di URL (`?program=<id>`) ============
/** Jendela detail program dibuka lewat query string supaya tautan dari Pusat
 *  Approval, notifikasi, dan detail Claim Batch langsung mendarat di program
 *  yang dimaksud. Kalau tautannya mendarat di halaman yang SALAH (mis. program
 *  kanal Dealer dibuka dari halaman toko sendiri, atau program lama), halaman
 *  pindah ke rute milik program itu (`program.rute`, dihitung backend) —
 *  bukan menampilkan program di halaman yang kolomnya tidak cocok. */
export function useProgramDiUrl(ruteIni) {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const programId = sp.get("program") || "";
  const [program, setProgram] = useState(null);
  const programRef = useRef(null);
  programRef.current = program;

  const hapusParam = useCallback(() => {
    setSp((prev) => { const n = new URLSearchParams(prev); n.delete("program"); return n; }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    if (!programId) { setProgram(null); return undefined; }
    if (programRef.current?.id === programId) return undefined;
    let batal = false;
    api.get(`${CB_BASE}/programs/${programId}`).then((r) => {
      if (batal) return;
      const p = r.data.item;
      if (p?.rute && ruteIni && p.rute !== ruteIni) {
        navigate(tautanProgram(p.rute, p.id), { replace: true });
        return;
      }
      setProgram(p);
    }).catch((e) => {
      if (batal) return;
      toast.error(e?.response?.data?.detail || "Program Price Protection tidak ditemukan");
      hapusParam();
    });
    return () => { batal = true; };
  }, [programId, ruteIni, navigate, hapusParam]);

  const buka = useCallback((p) => {
    setProgram(p);
    setSp((prev) => { const n = new URLSearchParams(prev); n.set("program", p.id); return n; }, { replace: true });
  }, [setSp]);
  const tutup = useCallback(() => { setProgram(null); hapusParam(); }, [hapusParam]);
  // Detail SEGAR dari backend (status, `hak`, ringkasan) — dipakai setelah aksi;
  // respons PUT/aksi hanya membawa dokumen mentah tanpa `hak`.
  const segarkan = useCallback(async () => {
    const id = programRef.current?.id;
    if (!id) return;
    try {
      const r = await api.get(`${CB_BASE}/programs/${id}`);
      setProgram(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat ulang program"); }
  }, []);
  return { program, buka, tutup, segarkan };
}

// ============ Halaman: Support Aktivasi / Support Cashback × toko sendiri / Dealer Partner ============
export default function CashbackProgram() {
  const { jenis, kanal } = useParams();
  if (![JENIS_AKTIVASI, JENIS_CASHBACK].includes(jenis) || ![KANAL_TOKO, KANAL_DEALER].includes(kanal)) {
    return <Navigate to={RUTE_DASHBOARD_PP} replace />;
  }
  // `key`: keempat halaman memakai SATU elemen rute — tanpa key, filter dan
  // jendela detail halaman sebelumnya ikut terbawa saat pindah submenu.
  return <HalamanProgram key={`${jenis}/${kanal}`} jenis={jenis} kanal={kanal} />;
}

function HalamanProgram({ jenis, kanal }) {
  const { can, branding } = useAuth();
  const navigate = useNavigate();
  const cabangOptions = useCabangOptions({ denganKosong: true });
  const namaToko = branding?.nama_toko_sendiri || "EppleHub";
  const aktivasi = jenis === JENIS_AKTIVASI;
  const judul = `${labelJenis(jenis)} ${labelKanal(kanal, namaToko)}`;

  const [sp, setSp] = useSearchParams();
  // Brief A.1: halaman Support Aktivasi TANPA tombol Dashboard — dashboard jadi
  // item submenu (keputusan A8). Cashback tetap punya (A.2 "tidak ada perubahan").
  const daftarView = aktivasi ? ["list", "laporan"] : ["dashboard", "list", "laporan"];
  const view = daftarView.includes(sp.get("view")) ? sp.get("view") : "list";
  const gantiView = (v) => setSp((prev) => {
    const n = new URLSearchParams(prev); n.set("view", v); n.delete("program"); return n;
  }, { replace: true });

  const [reload, setReload] = useState(0);
  const bump = () => setReload((n) => n + 1);
  const canCreate = can("finance_cashback_claim", "create");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const pd = useProgramDiUrl(ruteHalamanPP(jenis, kanal));

  const subjudul = aktivasi
    ? `Support aktivasi dari supplier untuk unit yang sudah terjual lewat ${kanal === KANAL_DEALER ? "Dealer Partner (pelanggan B2B)" : `penjualan ${namaToko}`} — nominal tetap per SKU, eligible menurut tanggal aktivasi.`
    : `Cashback IMEI saat supplier menurunkan harga — Harga Lama (STP Promo) − Harga Baru untuk unit yang terjual lewat ${kanal === KANAL_DEALER ? "Dealer Partner (pelanggan B2B)" : `penjualan ${namaToko}`}.`;

  const tombolView = [
    !aktivasi && { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "cb-prog-view-dashboard" },
    { key: "list", label: "Daftar Program", icon: ListFilter, tid: "cb-prog-view-daftar" },
    { key: "laporan", label: "Laporan Periode", icon: CalendarRange, tid: "cb-prog-view-laporan" },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-cashback-program">
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
            Program Price Protection · {aktivasi ? "Program Support Aktivasi" : "Program Support Cashback"}
          </p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2" data-testid="cb-prog-judul">
            <Percent className="w-7 h-7 text-emerald-600 shrink-0" /> {judul}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">{subjudul}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="bg-white border border-slate-200 rounded-lg p-1 inline-flex flex-wrap">
            {tombolView.map((t) => (
              <button key={t.key} onClick={() => gantiView(t.key)} data-testid={t.tid}
                      className={`h-8 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 whitespace-nowrap transition ${view === t.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {canCreate && view === "list" && (
            <button onClick={() => setShowForm(true)} data-testid="cb-prog-new"
                    className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Program Baru
            </button>
          )}
        </div>
      </div>

      {view === "dashboard" ? (
        <DashboardPriceProtection sorot={{ jenis, kanal }} reloadKey={reload} />
      ) : view === "laporan" ? (
        <LaporanPeriode jenis={jenis} kanal={kanal} />
      ) : (
        <DaftarProgramPanel jenis={jenis} kanal={kanal} reloadKey={reload} onOpen={pd.buka} />
      )}

      {showForm && (
        <FormProgram jenis={jenis} kanal={kanal} namaToko={namaToko} cabangOptions={cabangOptions}
                     onClose={() => setShowForm(false)}
                     onCreated={() => { setShowForm(false); bump(); }} />
      )}
      {editing && (
        <FormProgram program={editing} jenis={jenis} kanal={kanal} namaToko={namaToko} cabangOptions={cabangOptions}
                     onClose={() => setEditing(null)}
                     onCreated={() => { setEditing(null); bump(); pd.segarkan(); }} />
      )}
      {pd.program && (
        <ProgramDetailModal program={pd.program} onClose={pd.tutup}
                            onChanged={async () => { bump(); await pd.segarkan(); }}
                            onDeleted={() => { pd.tutup(); bump(); }}
                            onEdit={() => setEditing(pd.program)}
                            navigate={navigate} />
      )}
    </div>
  );
}

// ============ Daftar program (dipakai 4 halaman + tab Program Lama di dashboard) ============
const STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  { value: ST_PROGRAM_AKTIF, label: "Aktif" },
  { value: ST_PROGRAM_MENUNGGU, label: "Menunggu Approval Selesai" },
  { value: ST_PROGRAM_SELESAI, label: "Selesai" },
];

/** `jenis`: 'aktivasi' | 'cashback' | 'lama'. Total per program (A8) datang dari
 *  backend (`total_nominal` = nominal unit Eligible, termasuk yang sudah
 *  diklaim; program Selesai dari snapshot) — tidak dijumlah ulang di sini. */
export function DaftarProgramPanel({ jenis, kanal, reloadKey, onOpen }) {
  const aktivasi = jenis === JENIS_AKTIVASI;
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [supplierF, setSupplierF] = useState("");
  const qDeb = useDebounce(q, 400);
  const supplierDeb = useDebounce(supplierF, 400);

  const [items, setItems] = useState([]);
  const [totalNominal, setTotalNominal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [ulang, setUlang] = useState(0);
  const coba = () => setUlang((n) => n + 1);

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    (async () => {
      try {
        const params = { jenis };
        if (kanal) params.kanal = kanal;
        if (qDeb) params.q = qDeb;
        if (statusF) params.status = statusF;
        if (supplierDeb) params.supplier = supplierDeb;
        const r = await api.get(`${CB_BASE}/programs`, { params });
        if (batal) return;
        setItems(r.data.items || []);
        setTotalNominal(r.data.total_nominal || 0);
      } catch (e) {
        if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar Program Price Protection"); }
      } finally { if (!batal) setLoading(false); }
    })();
    return () => { batal = true; };
  }, [jenis, kanal, qDeb, statusF, supplierDeb, reloadKey, ulang]);

  const resetFilter = () => { setQ(""); setStatusF(""); setSupplierF(""); };
  const filterAktif = q || statusF || supplierF;
  const labelTotal = aktivasi ? "Total Support Aktivasi" : "Total Cashback";
  const tipTotal = "Nominal unit Eligible (termasuk yang sudah diajukan klaim). Program Selesai: angka dikunci saat disetujui.";

  // Urutan bawaan (created_at desc dari backend) tetap sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    kode: (it) => it.code || "",
    program: (it) => it.nama_program || "",
    brand: (it) => [it.brand, it.cabang].filter(Boolean).join(" · "),
    sku: (it) => (it.items || []).length,
    total: (it) => it.total_nominal ?? -1,
    status: (it) => it.status || "",
  });
  const kolom = 9;

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode, nama program, atau supplier..."
                   data-testid="cb-prog-search" className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 outline-none" />
          </div>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="cb-prog-filter-status"
                  className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[150px]">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input value={supplierF} onChange={(e) => setSupplierF(e.target.value)} placeholder="Filter supplier..." data-testid="cb-prog-filter-supplier"
                 className="h-10 px-3 border border-slate-200 rounded-lg text-sm min-w-[160px]" />
          {filterAktif && (
            <button onClick={resetFilter} data-testid="cb-prog-filter-reset"
                    className="h-10 px-3 rounded-lg text-slate-500 hover:text-slate-800 text-sm inline-flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="cb-prog-total" title={tipTotal}>
        <p className="text-sm text-emerald-900">{labelTotal} <span className="text-emerald-700">({items.length} program pada filter ini)</span></p>
        <p className="font-display text-xl font-bold text-emerald-800" data-testid="cb-prog-total-nominal">{idr(totalNominal)}</p>
      </div>

      <BatasDaftarNotice shown={items.length} batas={CB_PROGRAM_BATAS_SERVER} />
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <TabelDesktop>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label={aktivasi ? "Kode / Periode" : "Kode / Price Drop"} sortKeyName="kode" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-kode" />
                <SortTh label="Program / Supplier" sortKeyName="program" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-program" />
                <SortTh label={aktivasi ? "Produk / Cabang" : "Brand / Cabang"} sortKeyName="brand" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-brand" />
                <SortTh label="SKU" sortKeyName="sku" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-prog-sort-sku" />
                <th className="px-3 py-2.5 text-left">Periode Aktivasi</th>
                <th className="px-3 py-2.5 text-left">{aktivasi ? "Deadline Aktivasi" : "Deadline Submit"}</th>
                <SortTh label={labelTotal} sortKeyName="total" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="cb-prog-sort-total" />
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="cb-prog-sort-status" />
                <th className="px-3 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              <LoadingState loading={loading} error={loadError} onRetry={coba} kosong={!loading && !loadError && items.length === 0}
                            pesanKosong="Belum ada program pada filter ini" varian="tabel" kolom={kolom} data-testid="cb-prog-loading">
                {sortedItems.map((it) => (
                  <tr key={it.id} data-testid={`cb-prog-row-${it.id}`} onClick={() => onOpen(it)}
                      className="hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-3 py-3">
                      <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                      <p className="text-slate-800">{aktivasi ? `Program Periode ${it.label_periode || "-"}` : formatTanggal(it.tanggal_price_drop)}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium text-slate-900">{it.nama_program}</p>
                      <p className="text-[11px] text-slate-500">{it.supplier_nama}{it.pic_nama ? ` · PIC ${it.pic_nama}` : ""}</p>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-700">{[it.brand, it.cabang].filter(Boolean).join(" · ") || "-"}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{(it.items || []).length}</td>
                    <td className="px-3 py-3 text-xs text-slate-700">{formatTanggal(it.periode_aktivasi_dari)} s.d. {formatTanggal(it.periode_aktivasi_sampai)}</td>
                    <td className="px-3 py-3"><DeadlineBadge deadline={it.deadline_submit} statusBatch={ST_PROGRAM_BERJALAN.includes(it.status) ? "draft" : "lain"} /></td>
                    <td className="px-3 py-3 text-right font-mono font-semibold text-emerald-700" data-testid={`cb-prog-total-${it.id}`} title={tipTotal}>
                      {it.total_nominal == null ? "-" : idr(it.total_nominal)}
                    </td>
                    <td className="px-3 py-3"><ProgramStatusPill status={it.status} /></td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); onOpen(it); }}
                              data-testid={`cb-prog-open-${it.id}`} className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5 text-xs font-medium">
                        Kelola <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </TabelDesktop>

        <DaftarKartuMobile testid="cb-prog-kartu-mobile">
          <LoadingState loading={loading} error={loadError} onRetry={coba} kosong={!loading && !loadError && items.length === 0}
                        pesanKosong="Belum ada program pada filter ini" varian="kartu" data-testid="cb-prog-loading-m">
            {sortedItems.map((it) => (
              <KartuBaris key={it.id} testid={`cb-prog-card-${it.id}`} onClick={() => onOpen(it)}>
                <KartuBarisHeader
                  kiri={<>
                    <p className="font-mono text-[11px] text-slate-500">{it.code}</p>
                    <p className="text-sm font-semibold text-slate-900">{it.nama_program}</p>
                    <p className="text-[11px] text-slate-500">{it.supplier_nama}</p>
                  </>}
                  kanan={<ProgramStatusPill status={it.status} />}
                />
                <KartuBarisMeta label="SKU" value={(it.items || []).length} />
                {aktivasi
                  ? <KartuBarisMeta label="Periode" value={it.label_periode || "-"} />
                  : <KartuBarisMeta label="Price Drop" value={formatTanggal(it.tanggal_price_drop)} />}
                <KartuBarisMeta label={labelTotal} value={it.total_nominal == null ? "-" : idr(it.total_nominal)} tebal />
                <div className="flex items-center justify-between pt-1">
                  <DeadlineBadge deadline={it.deadline_submit} statusBatch={ST_PROGRAM_BERJALAN.includes(it.status) ? "draft" : "lain"} />
                  <span className="text-[11px] text-emerald-700 font-medium inline-flex items-center gap-0.5">
                    Kelola <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </KartuBaris>
            ))}
          </LoadingState>
        </DaftarKartuMobile>
      </div>
    </>
  );
}

// ============ Dashboard ============
// Angka funnel, recovery_rate, dan total per jenis & kanal SEPENUHNYA dihitung
// backend (`GET /cashback/dashboard`) — halaman ini murni menampilkan, tidak
// menjumlahkan ulang per-IMEI (aturan brief: jangan menghitung ulang cashback
// di klien). Dipakai item submenu "Dashboard" (A8) DAN tombol Dashboard di
// halaman Support Cashback (`sorot` menandai baris kanal halaman itu).
const METODE_LABEL = {
  credit_note: "Credit Note (CN)",
  kas_bank: "Kas/Bank Langsung",
  potong_utang_langsung: "Potong Utang Langsung",
};

export function DashboardPriceProtection({ sorot, reloadKey, onData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ulang, setUlang] = useState(0);
  const navigate = useNavigate();
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  useEffect(() => {
    let batal = false;
    setLoading(true); setError(null);
    api.get(`${CB_BASE}/dashboard`)
      .then((r) => { if (!batal) { setData(r.data); onDataRef.current?.(r.data); } })
      .catch((e) => { if (!batal) { setError(e); toast.error(e?.response?.data?.detail || "Gagal memuat dashboard Price Protection"); } })
      .finally(() => { if (!batal) setLoading(false); });
    return () => { batal = true; };
  }, [reloadKey, ulang]);

  if (loading) return <LoadingState loading varian="kartu" jumlahKartu={6} data-testid="cb-dash-loading" />;
  if (error || !data) return <LoadingState loading={false} error={error || "Gagal memuat"} onRetry={() => setUlang((n) => n + 1)} varian="kartu" data-testid="cb-dash-error" />;

  const f = data.funnel || {};
  const get = (k) => f[k] || { count: 0, total: 0 };
  const recovery = data.recovery_rate;
  const pp = data.price_protection || { per_jenis_kanal: [], total_cair: 0 };
  const baris = pp.per_jenis_kanal || [];
  // Penjumlahan per JENIS atas angka yang SUDAH dihitung server per kanal —
  // penyajian, bukan hitung ulang per unit.
  const jumlahJenis = (j) => round2(baris.filter((r) => r.jenis_program === j).reduce((a, r) => a + (r.total_eligible_selesai || 0), 0));
  const cairPerMetode = {};
  baris.forEach((r) => Object.entries(r.cair_per_metode || {}).forEach(([m, v]) => { cairPerMetode[m] = round2((cairPerMetode[m] || 0) + v); }));

  const cards = [
    { key: "potential", label: "Potensi", icon: Percent, tone: "text-slate-900", sub: "Support Aktivasi + Cashback semua unit program, termasuk yang belum aktif; unit stok dihitung sekali" },
    { key: "eligible", label: "Eligible (Siap Diklaim)", icon: CheckCircle2, tone: "text-emerald-700", sub: "belum masuk Claim Batch" },
    { key: "pending_activation", label: "Menunggu Aktivasi", icon: Clock, tone: "text-amber-700", sub: "belum ada tanggal aktivasi" },
    { key: "submitted", label: "Disubmit ke Supplier", icon: Send, tone: "text-sky-700", sub: "menunggu keputusan supplier" },
    { key: "approved", label: "Disetujui (Outstanding)", icon: Wallet, tone: "text-amber-700", sub: "menunggu settlement" },
    { key: "rejected", label: "Ditolak Supplier", icon: Ban, tone: "text-rose-700", sub: "bisa diajukan ulang (reclaim)" },
    { key: "received", label: "Diterima (Recovered)", icon: CheckCircle2, tone: "text-emerald-700", sub: "sudah settled/lunas" },
  ];

  const bukaBaris = (r) => {
    if (r.kanal === KANAL_TOKO || r.kanal === KANAL_DEALER) navigate(ruteHalamanPP(r.jenis_program, r.kanal));
    else navigate(RUTE_PROGRAM_LAMA);
  };

  return (
    <div className="space-y-6" data-testid="cb-dashboard">
      {/* A8: total program SELESAI (hak yang sudah dikunci) berdampingan dengan
          yang sudah cair dari supplier — selisihnya adalah yang belum ditagih. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="cb-pp-total-aktivasi">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Support Aktivasi</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-1">{idr(jumlahJenis(JENIS_AKTIVASI))}</p>
          <p className="text-[11px] text-slate-500 mt-1">nominal Eligible dari program yang sudah disetujui Selesai</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="cb-pp-total-cashback">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Cashback</p>
          <p className="font-display text-2xl font-bold text-slate-900 mt-1">{idr(jumlahJenis(JENIS_CASHBACK))}</p>
          <p className="text-[11px] text-slate-500 mt-1">nominal Eligible dari program yang sudah disetujui Selesai</p>
        </div>
        <div className="bg-emerald-700 text-white rounded-xl shadow-sm p-5" data-testid="cb-pp-total-cair">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-100">Sudah Cair dari Supplier</p>
          <p className="font-display text-2xl font-bold mt-1">{idr(pp.total_cair || 0)}</p>
          <div className="text-[11px] text-emerald-100 mt-1 space-y-0.5">
            {Object.entries(METODE_LABEL).map(([m, l]) => (
              <p key={m}>{l}: {idr(cairPerMetode[m] || 0)}</p>
            ))}
            <p className="text-emerald-200/80 pt-0.5">Credit Note = CN sudah diterbitkan; uang tunainya menyusul lewat menu Credit Note.</p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="cb-pp-per-kanal">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-800">Per Program & Kanal</p>
          <p className="text-[11px] text-slate-500">Klik baris untuk membuka halamannya.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Program</th>
                <th className="text-right px-3 py-2">Selesai</th>
                <th className="text-right px-3 py-2">Berjalan</th>
                <th className="text-right px-3 py-2">Unit Eligible (Selesai)</th>
                <th className="text-right px-3 py-2">Total Eligible (Selesai)</th>
                <th className="text-right px-3 py-2">Disetujui Supplier</th>
                <th className="text-right px-3 py-2">Sudah Cair</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {baris.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-6 text-sm">Belum ada program Price Protection</td></tr>
              )}
              {baris.map((r) => {
                const disorot = sorot && r.jenis_program === sorot.jenis && r.kanal === sorot.kanal;
                return (
                  <tr key={`${r.jenis_program}-${r.kanal}`} onClick={() => bukaBaris(r)} data-testid={`cb-pp-kanal-${r.jenis_program}-${r.kanal}`}
                      className={`cursor-pointer hover:bg-slate-50/70 ${disorot ? "bg-emerald-50/60" : ""}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-900">{r.label}</td>
                    <td className="px-3 py-2.5 text-right">{r.jumlah_program_selesai}</td>
                    <td className="px-3 py-2.5 text-right">{r.jumlah_program_berjalan}</td>
                    <td className="px-3 py-2.5 text-right">{r.jumlah_unit_eligible_selesai}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-slate-900">{idr(r.total_eligible_selesai)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(r.total_disetujui)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-700">{idr(r.total_cair)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-slate-800 mb-3">Pipeline Klaim (semua program)</p>
        {/* Missed/hangus SENGAJA dipisah dari grid KPI netral — brief butir 25:
            ini inti kerugian modul, tidak boleh terlihat setara angka lain. */}
        <div className="bg-rose-900 text-white rounded-xl shadow-lg p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="cb-kpi-missed">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/10 grid place-items-center shrink-0"><AlertTriangle className="w-6 h-6" /></div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">Hangus (Missed Claim)</p>
              <p className="font-display text-2xl md:text-3xl font-bold mt-0.5">{idr(get("missed").total)}</p>
              <p className="text-xs text-rose-200 mt-1">{get("missed").count} unit eligible yang TIDAK PERNAH disubmit sebelum deadline — kerugian murni yang sebenarnya bisa dicegah.</p>
            </div>
          </div>
          <div className="text-left sm:text-right shrink-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-200">Recovery Rate</p>
            <p className="font-display text-3xl font-bold mt-0.5">{recovery == null ? "-" : `${recovery}%`}</p>
            <p className="text-[11px] text-rose-200 mt-1">diterima ÷ (diterima + hangus)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          {cards.map((c) => (
            <div key={c.key} data-testid={`cb-kpi-${c.key}`} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-lg bg-slate-50 text-slate-700 grid place-items-center"><c.icon className="w-4.5 h-4.5" /></div>
                <span className="text-[10px] text-slate-400">{get(c.key).count} unit</span>
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{c.label}</p>
              <p className={`font-display text-xl font-bold mt-1 ${c.tone}`}>{idr(get(c.key).total)}</p>
              <p className="text-[11px] text-slate-500 mt-1">{c.sub}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-500">Per {data.as_of ? formatTanggal(data.as_of) : "-"} · {data.jumlah_program_aktif || 0} program aktif</p>
    </div>
  );
}

// ============ Laporan Periode (brief owner 19 Agu #10) ============
// Filter by rentang tanggal -> SKU apa saja yang TERCOVER Price Protection
// pada periode itu (periode_aktivasi program beririsan dengan rentang yang
// dipilih), lengkap dengan detail per unit: masuk tanggal berapa, terjual
// kemana — SEPENUHNYA dari backend (`GET /cashback/laporan/periode`), tidak
// dihitung ulang di klien. `jenis`/`kanal` (opsional, brief 22 Sep 2026)
// membatasi ke program halaman yang membukanya.
export function LaporanPeriode({ jenis, kanal }) {
  const bulanIni = hariIniISO().slice(0, 7);
  const [dari, setDari] = useState(`${bulanIni}-01`);
  const [sampai, setSampai] = useState(hariIniISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skuFilter, setSkuFilter] = useState("");
  const [expandedImei, setExpandedImei] = useState(() => new Set());
  const aktivasi = jenis === JENIS_AKTIVASI;

  const load = async () => {
    if (!dari || !sampai) return;
    if (sampai < dari) { toast.error("Tanggal sampai tidak boleh sebelum tanggal mulai"); return; }
    setLoading(true); setError(null);
    try {
      const params = { dari, sampai };
      if (jenis) params.jenis = jenis;
      if (kanal) params.kanal = kanal;
      const r = await api.get(`${CB_BASE}/laporan/periode`, { params });
      setData(r.data);
    } catch (e) { setError(e); toast.error(e?.response?.data?.detail || "Gagal memuat laporan periode"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Kolom modal hanya untuk laporan lintas program yang memang membawanya:
  // program ber-jenis tidak memakai modal (brief: kolom Modal DIHAPUS), dan
  // backend tidak mengirimnya kepada yang tidak punya akses modal (E28).
  const tampilModal = !!data && !data.modal_disembunyikan && jenis !== JENIS_AKTIVASI && jenis !== JENIS_CASHBACK;
  // Support Aktivasi & Cashback punya rumus sendiri (brief A.2) — backend
  // menjumlahnya di kolom terpisah; laporan lintas program (tanpa `jenis`)
  // menampilkan keduanya, halaman per jenis hanya kolomnya sendiri.
  const tampilAktivasi = !jenis || aktivasi;
  const tampilCashback = !aktivasi;
  const kolomSku = 5 + (tampilModal ? 1 : 0) + (tampilAktivasi ? 1 : 0) + (tampilCashback ? 1 : 0);

  const filteredItems = useMemo(() => {
    const all = data?.items || [];
    if (!skuFilter) return all;
    return all.filter((it) => it.sku === skuFilter);
  }, [data, skuFilter]);

  // Kedua tabel di laporan ini adalah hasil AGREGASI/rincian backend untuk
  // rentang tanggal yang dipilih (bukan potongan N-teratas dari query
  // mentah) — aman disortir tanpa BatasDaftarNotice. Baris trade-in
  // (`penjualan` >1x) tetap menempel ke IMEI induknya karena disortir sebagai
  // satu grup lewat array `items` di luar (`sortRows` hanya menata ULANG
  // urutan grup, tidak memecahnya).
  const skuSort = useSortState(null);
  const sortedSkus = sortRows(data?.skus || [], skuSort.sortKey, skuSort.sortDir, {
    sku: (s) => s.sku || "",
    produk: (s) => s.nama_produk || "",
    unit: (s) => s.jumlah_unit || 0,
    modal: (s) => s.total_modal || 0,
    aktivasi: (s) => s.total_support_aktivasi || 0,
    cashback: (s) => s.total_cashback || 0,
  });

  const itemSort = useSortState(null);
  const items = sortRows(filteredItems, itemSort.sortKey, itemSort.sortDir, {
    imei: (it) => it.imei || "",
    sku: (it) => it.sku || "",
    masuk: (it) => it.tanggal_masuk || "",
    modal: (it) => it.harga_perolehan || 0,
    status: (it) => it.status || "",
  });
  const kolomDetail = tampilModal ? 6 : 5;

  const toggleExpand = (imei) => setExpandedImei((prev) => { const n = new Set(prev); n.has(imei) ? n.delete(imei) : n.add(imei); return n; });

  return (
    <div className="space-y-4" data-testid="cb-laporan-periode">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Dari</span>
            <DatePicker value={dari} onChange={setDari} data-testid="cb-laporan-dari" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-medium">Sampai</span>
            <DatePicker value={sampai} onChange={setSampai} data-testid="cb-laporan-sampai" />
          </div>
          <button onClick={load} disabled={loading} data-testid="cb-laporan-terapkan"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            <CalendarRange className="w-4 h-4" /> {loading ? "Memuat..." : "Terapkan"}
          </button>
          <p className="text-xs text-slate-500">Periode aktivasi program yang beririsan dengan rentang ini{jenis ? " (hanya program halaman ini)" : ""}.</p>
        </div>
      </div>

      {error && <LoadingState loading={false} error={error} onRetry={load} varian="kartu" data-testid="cb-laporan-error" />}

      {!error && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Program Tercover</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.jumlah_program}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">SKU Tercover</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.skus.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total Unit</p>
              <p className="font-display text-2xl font-bold text-slate-900 mt-1">{data.count}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <PackageSearch className="w-4 h-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-800">SKU yang Tercover</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                  <tr>
                    <SortTh label="SKU" sortKeyName="sku" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} testid="cb-laporan-sku-sort-sku" />
                    <SortTh label="Produk" sortKeyName="produk" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} testid="cb-laporan-sku-sort-produk" />
                    <th className="text-left px-3 py-2">Program</th>
                    <SortTh label="Jumlah Unit" sortKeyName="unit" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-unit" />
                    {tampilModal && <SortTh label="Total Modal" sortKeyName="modal" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-modal" />}
                    {tampilAktivasi && <SortTh label={jenis ? "Total Support Aktivasi" : "Support Aktivasi"} sortKeyName="aktivasi" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-aktivasi" />}
                    {tampilCashback && <SortTh label={jenis ? "Total Cashback" : "Cashback"} sortKeyName="cashback" sortKey={skuSort.sortKey} sortDir={skuSort.sortDir} onSort={skuSort.toggle} align="right" testid="cb-laporan-sku-sort-cashback" />}
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {data.skus.length === 0 && (
                    <tr><td colSpan={kolomSku} className="text-center text-slate-400 py-6 text-sm">Tidak ada SKU tercover pada periode ini</td></tr>
                  )}
                  {sortedSkus.map((s) => (
                    <tr key={s.sku} data-testid={`cb-laporan-sku-${s.sku}`} className={skuFilter === s.sku ? "bg-emerald-50/50" : ""}>
                      <td className="px-3 py-2 font-mono text-xs">{s.sku}</td>
                      <td className="px-3 py-2">{s.nama_produk}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{s.program_codes.join(", ")}</td>
                      <td className="px-3 py-2 text-right">{s.jumlah_unit}</td>
                      {tampilModal && <td className="px-3 py-2 text-right font-mono">{idr(s.total_modal)}</td>}
                      {tampilAktivasi && <td className="px-3 py-2 text-right font-mono text-emerald-700">{idr(s.total_support_aktivasi)}</td>}
                      {tampilCashback && <td className="px-3 py-2 text-right font-mono text-emerald-700">{idr(s.total_cashback)}</td>}
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => setSkuFilter((cur) => cur === s.sku ? "" : s.sku)} data-testid={`cb-laporan-sku-filter-${s.sku}`}
                                className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium">
                          {skuFilter === s.sku ? "Lihat semua" : "Lihat detail"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800">Detail Unit{skuFilter ? ` — ${skuFilter}` : ""}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Masuk tanggal berapa dan terjual kemana, per IMEI.</p>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0">
                  <tr>
                    <SortTh label="IMEI" sortKeyName="imei" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-imei" />
                    <SortTh label="SKU / Produk" sortKeyName="sku" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-sku" />
                    <SortTh label="Masuk Tanggal" sortKeyName="masuk" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} testid="cb-laporan-detail-sort-masuk" />
                    {tampilModal && <SortTh label="Modal Aktual" sortKeyName="modal" sortKey={itemSort.sortKey} sortDir={itemSort.sortDir} onSort={itemSort.toggle} align="right" testid="cb-laporan-detail-sort-modal" />}
                    <th className="text-center px-2 py-2">Status</th>
                    <th className="text-left px-2 py-2">Terjual Ke</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  <LoadingState loading={loading} error={null} kosong={!loading && items.length === 0}
                                pesanKosong="Tidak ada unit pada filter ini" varian="tabel" kolom={kolomDetail} data-testid="cb-laporan-detail-loading">
                    {items.map((it) => {
                      const kunci = `${it.program_id}-${it.imei}`;
                      const expanded = expandedImei.has(kunci);
                      const penjualan = it.penjualan || [];
                      return (
                        <Fragment key={kunci}>
                          <tr data-testid={`cb-laporan-row-${it.imei}`}>
                            <td className="px-2 py-2 font-mono text-xs">{it.imei}</td>
                            <td className="px-2 py-2">
                              <p className="text-slate-900">{it.nama_produk}</p>
                              <p className="text-[10px] text-slate-500 font-mono">{it.sku} · {it.program_code}</p>
                            </td>
                            <td className="px-2 py-2 text-xs">{formatTanggal(it.tanggal_masuk)}</td>
                            {tampilModal && <td className="px-2 py-2 text-right font-mono">{idr(it.harga_perolehan)}</td>}
                            <td className="px-2 py-2 text-center"><EligibilityPill status={it.status} /></td>
                            <td className="px-2 py-2 text-xs">
                              {!it.sudah_terjual ? (
                                <span className="text-slate-400">Belum terjual</span>
                              ) : penjualan.length === 1 ? (
                                <span>{penjualan[0].nama_pelanggan || "-"} <span className="text-slate-400">· {formatTanggal(penjualan[0].tanggal)}{penjualan[0].branch ? ` · ${penjualan[0].branch}` : ""}</span></span>
                              ) : (
                                <button onClick={() => toggleExpand(kunci)} data-testid={`cb-laporan-expand-${it.imei}`}
                                        className="text-emerald-700 hover:text-emerald-900 font-medium">
                                  {penjualan.length}x terjual (riwayat tukar tambah) — {expanded ? "tutup" : "lihat"}
                                </button>
                              )}
                            </td>
                          </tr>
                          {expanded && penjualan.length > 1 && penjualan.map((p, i) => (
                            <tr key={`${kunci}-p-${i}`} className="bg-slate-50/60">
                              <td></td>
                              <td colSpan={kolomDetail - 1} className="px-2 py-1.5 text-xs text-slate-600">
                                {formatTanggal(p.tanggal)} — {p.nama_pelanggan || "-"}{p.branch ? ` · ${p.branch}` : ""}{p.nama_penjual ? ` · penjual: ${p.nama_penjual}` : ""}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </LoadingState>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ============ Form: Program Baru / Edit ============
const EMPTY_FORM = {
  nama_program: "", supplier_nama: "", brand: "", cabang: "", pic_user_id: "",
  items: [{ sku: "", harga_lama: "", harga_baru: "", nominal_aktivasi: "" }],
  tanggal_price_drop: "", periode_aktivasi_dari: "", periode_aktivasi_sampai: "",
  periode_klaim_dari: "", periode_klaim_sampai: "", deadline_submit: "",
  metode_settlement_default: "", keterangan: "",
};
const ITEM_KOSONG = { sku: "", harga_lama: "", harga_baru: "", nominal_aktivasi: "" };

/** Form per JENIS (brief A.1): Support Aktivasi TANPA Tanggal Price Drop & Harga
 *  Baru, "Harga Lama" jadi Nominal Support Aktivasi, label Deadline Aktivasi &
 *  Periode Klaim diperjelas (A6). Support Cashback: sama seperti dulu (A.2
 *  "tidak ada perubahan") + PIC. Jenis & kanal diambil dari halaman yang
 *  membukanya dan TIDAK bisa diubah sesudahnya (backend menolak). Program LAMA
 *  (tanpa jenis) tetap bisa diedit dengan form lamanya, tanpa PIC. */
export function FormProgram({ program, jenis, kanal, namaToko, cabangOptions, onClose, onCreated }) {
  const isEdit = !!program;
  const lama = isEdit && !!program.program_lama;
  const jenisF = isEdit ? program.jenis_program : jenis;
  const kanalF = isEdit ? program.kanal : kanal;
  const aktivasi = !lama && jenisF === JENIS_AKTIVASI;

  const [f, setF] = useState(() => program ? {
    nama_program: program.nama_program, supplier_nama: program.supplier_nama,
    brand: program.brand || "", cabang: program.cabang || "", pic_user_id: program.pic_user_id || "",
    items: (program.items || []).map((it) => ({
      sku: it.sku, harga_lama: it.harga_lama ?? "", harga_baru: it.harga_baru ?? "", nominal_aktivasi: it.nominal_aktivasi ?? "",
    })),
    tanggal_price_drop: program.tanggal_price_drop || "", periode_aktivasi_dari: program.periode_aktivasi_dari || "",
    periode_aktivasi_sampai: program.periode_aktivasi_sampai || "",
    periode_klaim_dari: program.periode_klaim_dari || "", periode_klaim_sampai: program.periode_klaim_sampai || "",
    deadline_submit: program.deadline_submit || "", metode_settlement_default: program.metode_settlement_default || "",
    keterangan: program.keterangan || "",
  } : { ...EMPTY_FORM, tanggal_price_drop: aktivasi ? "" : hariIniISO(), items: [{ ...ITEM_KOSONG }] });
  const [busy, setBusy] = useState(false);
  const [katalog, setKatalog] = useState([]);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [brandOpts, setBrandOpts] = useState([]);
  const [picOpts, setPicOpts] = useState([]);

  // Katalog SKU untuk autocomplete — endpoint ini HANYA butuh login (tidak
  // digerbangi izin `inventory_produk`), sengaja dipilih ketimbang `/inventory/
  // products/lookup` supaya custom role yang cuma dikasih `finance_cashback_claim`
  // (tanpa izin Inventory) tetap bisa memilih SKU tanpa 403.
  useEffect(() => {
    api.get("/finance/persediaan/products").then((r) => setKatalog(r.data.items || [])).catch(() => {});
  }, []);

  // Supplier & Brand (brief owner 19 Agu #8): dropdown TERBATAS ke master —
  // "hanya bisa memilih supplier dan brand terdaftar". Endpoint RINGKAS milik
  // modul ini sendiri (`/cashback/master/...`, gerbang `finance_cashback_claim.
  // view`), BUKAN `/supplier` atau `/inventory/master-opsi` langsung — kedua
  // itu digerbangi izin Inventory yang tidak tentu dipegang pengguna Finance
  // (lihat docstring backend `daftar_supplier_untuk_form`/`daftar_brand_untuk_form`).
  // PIC (keputusan A7) dari endpoint yang sama polanya: hanya pengguna yang
  // punya izin mengubah program — PIC tanpa izin itu tidak akan bisa menekan
  // Setujui Selesai.
  useEffect(() => {
    api.get(`${CB_BASE}/master/supplier`).then((r) => setSupplierOpts(r.data.items || [])).catch(() => {});
    api.get(`${CB_BASE}/master/brand`).then((r) => setBrandOpts(r.data.items || [])).catch(() => {});
    if (!lama) api.get(`${CB_BASE}/master/pic`).then((r) => setPicOpts(r.data.items || [])).catch(() => {});
  }, [lama]);

  // Kompatibilitas mundur: program LAMA bisa punya `supplier_nama`/`brand`
  // berupa teks yang TIDAK persis cocok dengan master (beda ejaan/besar-kecil,
  // atau supplier/brand yang sudah dihapus/diganti nama). Kalau dropdown HANYA
  // berisi opsi master, membuka form edit akan diam-diam menjatuhkan pilihan ke
  // opsi lain (mengubah data tanpa disadari staf) atau membuat dropdown
  // tampak kosong. Nilai lama yang tidak ketemu di master disisipkan sebagai
  // opsi tambahan berlabel jelas, supaya form terbuka dengan nilai yang BENAR
  // dan hanya berubah kalau staf sendiri memilih opsi lain.
  const supplierNames = useMemo(() => supplierOpts.map((s) => s.nama), [supplierOpts]);
  // Cocokkan EXACT dulu (`<select>` HTML case-sensitive terhadap `value` —
  // kalau nilai lama cuma beda huruf besar-kecil dari opsi master, native
  // select tidak akan menandai opsi manapun terpilih walau datanya "cocok").
  // Peringatan "(nilai lama, tidak di master)" HANYA muncul kalau memang
  // tidak ketemu sama sekali (case-insensitive) — beda besar-kecil semata
  // BUKAN masalah nyata (backend menormalisasi ke ejaan kanonik begitu staf
  // menyentuhnya), jadi tidak perlu menakut-nakuti dengan label itu.
  const supplierSelectOpts = useMemo(() => {
    if (isEdit && program.supplier_nama && !supplierNames.includes(program.supplier_nama)) {
      const benarBenarTakDikenal = !supplierNames.some((n) => n.toLowerCase() === program.supplier_nama.toLowerCase());
      const label = benarBenarTakDikenal ? `${program.supplier_nama} (nilai lama, tidak di master)` : program.supplier_nama;
      return [{ value: program.supplier_nama, label }, ...supplierNames.map((n) => ({ value: n, label: n }))];
    }
    return supplierNames.map((n) => ({ value: n, label: n }));
  }, [supplierNames, isEdit, program]);
  const brandSelectOpts = useMemo(() => {
    if (isEdit && program.brand && !brandOpts.includes(program.brand)) {
      const benarBenarTakDikenal = !brandOpts.some((n) => n.toLowerCase() === program.brand.toLowerCase());
      const label = benarBenarTakDikenal ? `${program.brand} (nilai lama, tidak di master)` : program.brand;
      return [{ value: program.brand, label }, ...brandOpts.map((n) => ({ value: n, label: n }))];
    }
    return brandOpts.map((n) => ({ value: n, label: n }));
  }, [brandOpts, isEdit, program]);
  // PIC lama yang sudah kehilangan izin tetap tampil (terpilih) supaya form
  // tidak diam-diam mengganti PIC; backend menolak kalau staf menyimpan PIC itu.
  const picSelectOpts = useMemo(() => {
    // Backend hanya mengirim {id, name} — email/role pengguna bukan untuk pemegang izin lihat.
    const opsi = picOpts.map((u) => ({ value: u.id, label: u.name }));
    if (isEdit && program.pic_user_id && !picOpts.some((u) => u.id === program.pic_user_id)) {
      return [{ value: program.pic_user_id, label: `${program.pic_nama || program.pic_email || "PIC lama"} (tidak lagi berizin)` }, ...opsi];
    }
    return opsi;
  }, [picOpts, isEdit, program]);

  const setItem = (idx, patch) => setF((prev) => ({ ...prev, items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setF((prev) => ({ ...prev, items: [...prev.items, { ...ITEM_KOSONG }] }));
  const delItem = (idx) => setF((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

  const skuNorm = f.items.map((it) => (it.sku || "").trim().toUpperCase());
  const adaSkuKembar = !lama && skuNorm.some((s, i) => s && skuNorm.indexOf(s) !== i);
  const itemsValid = f.items.length > 0 && !adaSkuKembar && f.items.every((it) => (it.sku || "").trim() && (aktivasi
    ? Number(it.nominal_aktivasi) > 0
    : Number(it.harga_lama) > 0 && Number(it.harga_baru) > 0 && Number(it.harga_baru) < Number(it.harga_lama)));
  const valid = f.nama_program.trim() && f.supplier_nama.trim() && f.periode_aktivasi_sampai && f.deadline_submit && itemsValid
    && (lama || f.pic_user_id)
    && (aktivasi ? !!f.periode_aktivasi_dari : !!f.tanggal_price_drop);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const body = {
        nama_program: f.nama_program.trim(), supplier_nama: f.supplier_nama.trim(),
        // STRING KOSONG, bukan null — alasan yang sama dengan periode klaim di
        // bawah: backend membaca null sebagai "tidak disentuh". Sejak brief 22 Sep
        // Cabang program toko MENYARING unit (A3), jadi Cabang yang tidak bisa
        // dikosongkan lewat edit = unit cabang lain tidak pernah masuk program,
        // padahal layar menampilkan "Program diperbarui ✓".
        brand: f.brand.trim() || "", cabang: f.cabang || "",
        items: f.items.map((it) => (aktivasi
          ? { sku: it.sku.trim().toUpperCase(), nominal_aktivasi: Number(it.nominal_aktivasi) }
          : { sku: it.sku.trim().toUpperCase(), harga_lama: Number(it.harga_lama), harga_baru: Number(it.harga_baru) })),
        periode_aktivasi_dari: f.periode_aktivasi_dari || undefined,
        periode_aktivasi_sampai: f.periode_aktivasi_sampai,
        // Kirim STRING KOSONG, bukan null, saat pengguna mengosongkan periode
        // klaim. Backend memperlakukan `null` sebagai "field tidak disertakan
        // permintaan ini" (pola `if body.x is not None` di `update_program`),
        // jadi mengirim null membuat periode yang SUDAH terisi mustahil
        // dihapus lewat form edit — sekali diisi, terkunci selamanya. String
        // kosong dibaca `_tanggal_opsional()` sebagai "kosongkan field ini".
        // Baru terasa sejak 21 Agu 2026, saat field ini mulai memicu peringatan
        // klaim di luar periode; sebelumnya ia tersimpan tapi tidak dipakai
        // apa pun sehingga tidak ada yang sadar tidak bisa dihapus.
        periode_klaim_dari: f.periode_klaim_dari || "", periode_klaim_sampai: f.periode_klaim_sampai || "",
        deadline_submit: f.deadline_submit,
        metode_settlement_default: f.metode_settlement_default || "",
        keterangan: f.keterangan || "",
      };
      // Brief A.1: Support Aktivasi tidak memakai Tanggal Price Drop — medannya
      // tidak dikirim sama sekali (backend menolak kalau terisi).
      if (!aktivasi) body.tanggal_price_drop = f.tanggal_price_drop;
      if (!lama) {
        body.jenis_program = jenisF;
        body.kanal = kanalF;
        body.pic_user_id = f.pic_user_id;
      }
      const r = isEdit ? await api.put(`${CB_BASE}/programs/${program.id}`, body) : await api.post(`${CB_BASE}/programs`, body);
      toast.success(r.data.message || "Program tersimpan ✓");
      onCreated?.(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan Program Price Protection"); }
    finally { setBusy(false); }
  };

  const judulJenis = lama ? "Program Price Protection" : `${labelJenis(jenisF)} ${labelKanal(kanalF, namaToko)}`;

  return (
    <Modal title={isEdit ? `Edit Program ${program.code}` : `Program Baru — ${judulJenis}`} icon={Percent}
           onClose={onClose} onSubmit={submit} submitLabel={isEdit ? "Simpan Perubahan" : "Buat Program"}
           busy={busy} disabled={!valid} testid="cb-prog-form-modal" wide
           // BERBAHAYA: daftar SKU + nominal dinamis, bisa berbaris-baris —
           // menutup tanpa sengaja berarti mengetik ulang semuanya (brief owner 21 Agu).
           dangerous>
      {!lama && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="cb-prog-form-jenis-kanal">
          <span className="px-2 py-1 rounded-md bg-slate-900 text-white font-medium">{labelJenis(jenisF)}</span>
          <span className="px-2 py-1 rounded-md bg-slate-100 text-slate-700 font-medium">{labelKanal(kanalF, namaToko)}</span>
          <span className="text-slate-500">
            {aktivasi ? "Nominal tetap per SKU." : "Nominal = Harga Lama (STP Promo) − Harga Baru."}
            {" "}Hanya unit yang terjual lewat {kanalF === KANAL_DEALER ? "pelanggan B2B (Dealer Partner)" : `penjualan ${namaToko}`} yang masuk.
          </span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Nama Program" col2>
          <input value={f.nama_program} onChange={(e) => setF({ ...f, nama_program: e.target.value })} data-testid="cb-prog-form-nama" className={inputCls}
                 placeholder={aktivasi ? "mis. Support Aktivasi September 2026" : "mis. Price Protection Agustus 2026"} />
        </Field>
        <Field label="Nama Supplier" hint={supplierSelectOpts.length === 0 ? "Belum ada supplier terdaftar — daftarkan dulu lewat Master Supplier" : undefined}>
          <select value={f.supplier_nama} onChange={(e) => setF({ ...f, supplier_nama: e.target.value })} data-testid="cb-prog-form-supplier" className={inputCls}>
            <option value="">Pilih supplier...</option>
            {supplierSelectOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        {/* A6: "Produk" di brief = Brand Produk (opsional). */}
        <Field label={aktivasi ? "Produk / Brand (opsional)" : "Brand Produk (opsional)"}>
          <select value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="cb-prog-form-brand" className={inputCls}>
            <option value="">- Tidak spesifik -</option>
            {brandSelectOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Cabang (opsional)"
               hint={lama ? undefined : kanalF === KANAL_TOKO
                 ? "Diisi = hanya unit yang terjual di cabang ini yang masuk program."
                 : "Hanya keterangan — penjualan Dealer Partner tidak disaring per cabang."}>
          <select value={f.cabang} onChange={(e) => setF({ ...f, cabang: e.target.value })} data-testid="cb-prog-form-cabang" className={inputCls}>
            {cabangOptions.map((c) => <option key={c || "_none"} value={c}>{c || "-"}</option>)}
          </select>
        </Field>
        {!lama && (
          <Field label="PIC (penyetuju Selesai)" hint={picSelectOpts.length === 0 ? "Belum ada pengguna yang berizin mengubah Program Price Protection" : "Menyetujui program ini Selesai. Finance SPV/CEO juga boleh menyetujui."}>
            <select value={f.pic_user_id} onChange={(e) => setF({ ...f, pic_user_id: e.target.value })} data-testid="cb-prog-form-pic" className={inputCls}>
              <option value="">Pilih PIC...</option>
              {picSelectOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        )}
        {!aktivasi && (
          <Field label="Tanggal Price Drop">
            <DatePicker value={f.tanggal_price_drop} onChange={(v) => setF({ ...f, tanggal_price_drop: v })} data-testid="cb-prog-form-tgl-drop" />
          </Field>
        )}
        {/* Aktivasi tidak punya Tanggal Price Drop — Deadline selebar dua kolom
            supaya pasangan Periode Aktivasi & Periode Klaim tetap sebaris. */}
        <Field label={aktivasi ? "Deadline Aktivasi" : "Deadline Submit Klaim"} col2={aktivasi}
               hint={aktivasi
                 ? "Batas kirim data aktivasi ke supplier — lewat tanggal ini, support hangus. Pengingat H-14/7/3/1."
                 : "Batas kirim ke supplier — lewat tanggal ini, cashback hangus."}>
          <DatePicker value={f.deadline_submit} onChange={(v) => setF({ ...f, deadline_submit: v })} data-testid="cb-prog-form-deadline" />
        </Field>
        <Field label="Periode Aktivasi Dari" hint={aktivasi ? undefined : "Kosongkan = sama dengan tanggal price drop"}>
          <DatePicker value={f.periode_aktivasi_dari} onChange={(v) => setF({ ...f, periode_aktivasi_dari: v })} data-testid="cb-prog-form-periode-dari" />
        </Field>
        <Field label="Periode Aktivasi Sampai">
          <DatePicker value={f.periode_aktivasi_sampai} onChange={(v) => setF({ ...f, periode_aktivasi_sampai: v })} data-testid="cb-prog-form-periode-sampai" />
        </Field>
        <Field label={aktivasi ? "Periode Klaim dari (data sudah dikirim ke supplier)" : "Periode Klaim Dari (opsional)"}>
          <DatePicker value={f.periode_klaim_dari} onChange={(v) => setF({ ...f, periode_klaim_dari: v })} data-testid="cb-prog-form-klaim-dari" />
        </Field>
        <Field label={aktivasi ? "Periode Klaim sampai (CN cair)" : "Periode Klaim Sampai (opsional)"}>
          <DatePicker value={f.periode_klaim_sampai} onChange={(v) => setF({ ...f, periode_klaim_sampai: v })} data-testid="cb-prog-form-klaim-sampai" />
        </Field>
        <Field label="Metode Settlement Default (opsional)" col2>
          <select value={f.metode_settlement_default} onChange={(e) => setF({ ...f, metode_settlement_default: e.target.value })} data-testid="cb-prog-form-metode" className={inputCls}>
            <option value="">Belum ditentukan</option>
            {Object.entries(METODE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Field>
        <Field label="Keterangan (opsional)" col2>
          <textarea rows={2} value={f.keterangan} onChange={(e) => setF({ ...f, keterangan: e.target.value })} data-testid="cb-prog-form-ket" className={inputCls + " h-auto py-2"} />
        </Field>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-800">{aktivasi ? "SKU & Nominal Support Aktivasi" : "Produk / SKU yang Price Drop"}</p>
          <button type="button" onClick={addItem} data-testid="cb-prog-form-item-add"
                  className="text-xs font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
            <PlusCircle className="w-3.5 h-3.5" /> Tambah SKU
          </button>
        </div>
        <datalist id="cb-sku-datalist">
          {katalog.map((p) => <option key={p.sku} value={p.sku}>{p.nama} {p.brand ? `(${p.brand})` : ""}</option>)}
        </datalist>
        {adaSkuKembar && (
          <p className="text-[11px] text-rose-600 mb-2" data-testid="cb-prog-form-sku-kembar">Satu SKU hanya boleh tercantum sekali — satu nominal per SKU per program.</p>
        )}
        <div className="space-y-2">
          {f.items.map((it, idx) => {
            const hargaTidakValid = !aktivasi && it.harga_lama && it.harga_baru && Number(it.harga_baru) >= Number(it.harga_lama);
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                <div className={aktivasi ? "col-span-7" : "col-span-5"}>
                  <input list="cb-sku-datalist" value={it.sku} onChange={(e) => setItem(idx, { sku: e.target.value })}
                         placeholder="SKU" data-testid={`cb-prog-form-item-sku-${idx}`} className={inputCls} />
                </div>
                {aktivasi ? (
                  <div className="col-span-4">
                    <InputRupiah value={it.nominal_aktivasi} onChange={(n) => setItem(idx, { nominal_aktivasi: n })} placeholder="Nominal Support Aktivasi"
                                 testid={`cb-prog-form-item-nominal-${idx}`} className={inputCls} />
                  </div>
                ) : (
                  <>
                    <div className="col-span-3">
                      <InputRupiah value={it.harga_lama} onChange={(n) => setItem(idx, { harga_lama: n })} placeholder="Harga lama (STP Promo)"
                                   testid={`cb-prog-form-item-lama-${idx}`} className={inputCls} />
                    </div>
                    <div className="col-span-3">
                      <InputRupiah value={it.harga_baru} onChange={(n) => setItem(idx, { harga_baru: n })} placeholder="Harga baru"
                                   testid={`cb-prog-form-item-baru-${idx}`} className={inputCls} />
                    </div>
                  </>
                )}
                <div className="col-span-1 flex items-center h-10">
                  {f.items.length > 1 && (
                    <button type="button" onClick={() => delItem(idx)} data-testid={`cb-prog-form-item-del-${idx}`}
                            className="text-rose-500 hover:text-rose-700"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                {hargaTidakValid && (
                  <p className="col-span-12 text-[11px] text-rose-600 -mt-1">Harga baru harus lebih rendah dari harga lama (kalau tidak ada penurunan, tidak ada cashback).</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ============ Detail Program: unit, status Selesai, info, lampiran ============
export function ProgramDetailModal({ program, onClose, onChanged, onDeleted, onEdit, navigate }) {
  const { can, user } = useAuth();
  const [tab, setTab] = useState("kandidat");
  const [showAtt, setShowAtt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aksi, setAksi] = useState(null); // "ajukan" | "setujui" | "tolak" | "buka"

  const hak = program.hak || {};
  const canDelete = can("finance_cashback_claim", "delete");
  const lama = !!program.program_lama;
  const aktivasi = !lama && program.jenis_program === JENIS_AKTIVASI;
  const labelNominal = aktivasi ? "Support Aktivasi" : "Cashback";
  const aj = program.pengajuan_selesai || {};
  const snap = program.snapshot_selesai || null;
  const sayaPengaju = !!aj.oleh_id && aj.oleh_id === user?.id;

  const jalankan = async (fn, pesanGagal) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r.data.message);
      setAksi(null);
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || pesanGagal); }
    finally { setBusy(false); }
  };

  const doHapus = async () => {
    if (!window.confirm(`Hapus program ${program.code}? Aksi ini tidak bisa dibatalkan.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`${CB_BASE}/programs/${program.id}`);
      toast.success(r.data.message);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus program"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cb-prog-detail-modal"
           className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-slate-500">{program.code}{program.judul ? ` · ${program.judul}` : ""}</p>
            <h3 className="font-display font-bold text-lg text-slate-900 mt-0.5">{program.nama_program}</h3>
            <p className="text-sm text-slate-600">
              {program.supplier_nama}{program.brand ? ` · ${program.brand}` : ""}{program.cabang ? ` · ${program.cabang}` : ""}
              {program.label_periode ? ` · Program Periode ${program.label_periode}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ProgramStatusPill status={program.status} />
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="cb-prog-detail-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <StatusSelesaiBar program={program} hak={hak} aj={aj} snap={snap} busy={busy} sayaPengaju={sayaPengaju}
                          labelNominal={labelNominal} onAksi={setAksi} />

        <div className="border-b border-slate-100 flex items-center gap-1 px-2 overflow-x-auto">
          {[
            { key: "kandidat", label: "Unit & Eligibility" },
            { key: "info", label: "Info Program" },
            { key: "lampiran", label: "Lampiran" },
            { key: "riwayat", label: "Riwayat Status" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`cb-prog-tab-${t.key}`}
                    className={`px-4 h-11 text-sm font-medium border-b-2 whitespace-nowrap transition ${tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {tab === "kandidat" && (
            <KandidatManager program={program} navigate={navigate} onClaimCreated={onClose} onUnitBerubah={onChanged} />
          )}

          {tab === "info" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!lama && <InfoRow label="Jenis & Kanal" value={`${program.label_jenis} · ${program.label_kanal}`} />}
                {!lama && <InfoRow label="PIC" value={program.pic_nama || program.pic_email || "-"} />}
                {!aktivasi && <InfoRow label="Tanggal Price Drop" value={formatTanggal(program.tanggal_price_drop)} />}
                <InfoRow label={aktivasi ? "Deadline Aktivasi" : "Deadline Submit"} value={formatTanggal(program.deadline_submit)} />
                <InfoRow label="Periode Aktivasi" value={`${formatTanggal(program.periode_aktivasi_dari)} s.d. ${formatTanggal(program.periode_aktivasi_sampai)}`} />
                <InfoRow label={aktivasi ? "Periode Klaim (data dikirim → CN cair)" : "Periode Klaim"}
                         value={program.periode_klaim_dari || program.periode_klaim_sampai ? `${formatTanggal(program.periode_klaim_dari)} s.d. ${formatTanggal(program.periode_klaim_sampai)}` : "-"} />
                <InfoRow label="Metode Settlement Default" value={METODE_LABEL[program.metode_settlement_default] || "-"} />
                <InfoRow label="Dibuat oleh" value={`${program.created_by_name || program.created_by || "-"} · ${formatTanggal(program.created_at, { denganJam: true })}`} />
                {program.keterangan && <InfoRow label="Keterangan" value={program.keterangan} col2 />}
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{aktivasi ? "SKU & Nominal Support Aktivasi" : "Daftar SKU Price Drop"}</p>
                <div className="border border-slate-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
                      {aktivasi ? (
                        <tr><th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Produk</th><th className="text-right px-3 py-2">Nominal Support Aktivasi</th></tr>
                      ) : (
                        <tr><th className="text-left px-3 py-2">SKU</th><th className="text-left px-3 py-2">Produk</th><th className="text-right px-3 py-2">{lama ? "Harga Lama" : "STP Promo (Harga Lama)"}</th><th className="text-right px-3 py-2">Harga Baru</th><th className="text-right px-3 py-2">{lama ? "Selisih" : "Nominal Cashback"}</th></tr>
                      )}
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {(program.items || []).map((it) => (
                        <tr key={it.sku}>
                          <td className="px-3 py-2 font-mono text-xs">{it.sku}</td>
                          <td className="px-3 py-2">{it.nama_produk}</td>
                          {aktivasi ? (
                            <td className="px-3 py-2 text-right font-mono text-emerald-700">{idr(it.nominal_aktivasi)}</td>
                          ) : (
                            <>
                              <td className="px-3 py-2 text-right font-mono">{idr(it.harga_lama)}</td>
                              <td className="px-3 py-2 text-right font-mono">{idr(it.harga_baru)}</td>
                              <td className="px-3 py-2 text-right font-mono text-emerald-700">{idr(it.harga_lama - it.harga_baru)}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {lama && (
                  <p className="text-[11px] text-slate-500 mt-1.5">Program lama: cashback per unit dihitung dari modal ASLI unit − harga baru (rumus sebelum 22 Sep 2026, tidak berubah).</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
                {hak.boleh_ubah && <ActionBtn icon={Pencil} label="Edit Program" onClick={onEdit} testid="cb-prog-act-edit" />}
                {canDelete && program.status === ST_PROGRAM_AKTIF && (
                  <ActionBtn icon={Trash2} label="Hapus Program" onClick={doHapus} testid="cb-prog-act-hapus" tone="rose" disabled={busy} />
                )}
                {!hak.boleh_ubah && program.status !== ST_PROGRAM_AKTIF && (
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Program terkunci — tidak bisa diedit selama menunggu persetujuan atau sesudah Selesai.</p>
                )}
              </div>
            </div>
          )}

          {tab === "lampiran" && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Lampirkan memo/surat program dari supplier sebagai bukti dasar program ini.</p>
              <button onClick={() => setShowAtt(true)} data-testid="cb-prog-open-attachments"
                      className="h-10 px-4 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Kelola Lampiran (Memo Supplier)
              </button>
            </div>
          )}

          {tab === "riwayat" && (
            <div className="space-y-2" data-testid="cb-prog-riwayat">
              {[...(program.riwayat_status || [])].sort((a, b) => (b.at || "").localeCompare(a.at || "")).map((r, i) => (
                <div key={i} className="flex items-start gap-3 border-b border-slate-50 pb-2 last:border-0">
                  <History className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ProgramStatusPill status={r.status} />
                      <span className="text-xs text-slate-500">{formatTanggal(r.at, { denganJam: true })}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5">oleh {r.by || "-"}{r.catatan ? ` — ${r.catatan}` : ""}</p>
                  </div>
                </div>
              ))}
              {!(program.riwayat_status || []).length && <p className="text-sm text-slate-400 py-6 text-center">Belum ada riwayat status (program lama).</p>}
            </div>
          )}
        </div>
      </div>

      {showAtt && (
        <AttachmentPanel module="cashback_program" objId={program.id} objLabel={`${program.code} · ${program.nama_program}`}
                          onClose={() => setShowAtt(false)} />
      )}

      {aksi === "ajukan" && (
        <KonfirmasiCatatanModal title="Ajukan Program Selesai" icon={Lock} submitLabel="Ajukan Selesai" testid="cb-prog-ajukan-modal"
                                onClose={() => setAksi(null)}
                                onSubmit={(catatan) => jalankan(() => api.post(`${CB_BASE}/programs/${program.id}/ajukan-selesai`, { catatan }), "Gagal mengajukan Selesai")}>
          <p className="text-sm text-slate-600">
            Pengajuan dikirim ke <b>{program.pic_nama || "PIC program"}</b> atau Finance SPV/CEO. Anda sendiri tidak bisa menyetujuinya.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
            <p>Sejak diajukan, program TERKUNCI: tidak bisa diedit, diimpor tanggal aktivasinya, atau dipindah unitnya.</p>
            <p>Setelah disetujui, Total {labelNominal} Eligible disimpan sebagai snapshot dan muncul di Dashboard. Klaim yang sudah ada tetap bisa diproses.</p>
          </div>
          <p className="text-sm text-slate-700">Total {labelNominal} Eligible saat ini: <b>{idr(program.total_nominal ?? program.ringkasan?.total_eligible ?? 0)}</b></p>
        </KonfirmasiCatatanModal>
      )}
      {aksi === "setujui" && (
        <KonfirmasiCatatanModal title="Setujui Program Selesai" icon={CheckCircle2} submitLabel="Setujui Selesai" testid="cb-prog-setujui-modal"
                                onClose={() => setAksi(null)}
                                onSubmit={(catatan) => jalankan(() => api.post(`${CB_BASE}/programs/${program.id}/setujui-selesai`, { catatan }), "Gagal menyetujui Selesai")}>
          <p className="text-sm text-slate-600">
            Diajukan oleh <b>{aj.oleh_nama || "-"}</b> {aj.at ? `pada ${formatTanggal(aj.at, { denganJam: true })}` : ""}.
            {aj.catatan ? ` Catatan: "${aj.catatan}".` : ""}
          </p>
          <p className="text-sm text-slate-700">Total saat diajukan: <b>{idr(aj.ringkasan?.total_eligible ?? 0)}</b> ({aj.ringkasan?.jumlah_eligible ?? 0} unit eligible). Angka final dihitung ulang saat Anda menyetujui, lalu dikunci.</p>
        </KonfirmasiCatatanModal>
      )}
      {aksi === "tolak" && (
        <ReasonModal title={sayaPengaju && !hak.boleh_setujui_selesai ? "Tarik Pengajuan Selesai" : "Tolak Pengajuan Selesai"} icon={Undo2}
                     label="Alasan (wajib)" submitLabel={sayaPengaju && !hak.boleh_setujui_selesai ? "Tarik Pengajuan" : "Tolak"}
                     testid="cb-prog-tolak-modal" onClose={() => setAksi(null)}
                     onSubmit={(alasan) => jalankan(() => api.post(`${CB_BASE}/programs/${program.id}/tolak-selesai`, { alasan }), "Gagal memproses pengajuan")}>
          <p className="text-sm text-slate-600">Program kembali Aktif dan bisa diubah lagi.</p>
        </ReasonModal>
      )}
      {aksi === "buka" && (
        <ReasonModal title="Buka Kembali Program" icon={LockOpen} label="Alasan membuka kembali (wajib, tercatat)" submitLabel="Buka Kembali"
                     testid="cb-prog-buka-modal" onClose={() => setAksi(null)}
                     onSubmit={(alasan) => jalankan(() => api.post(`${CB_BASE}/programs/${program.id}/buka-kembali`, { alasan }), "Gagal membuka kembali program")}>
          <p className="text-sm text-slate-600">Snapshot yang dikunci disimpan sebagai riwayat; angka program kembali dihitung dari data terkini sampai diajukan Selesai lagi.</p>
        </ReasonModal>
      )}
    </div>
  );
}

/** Bilah status Aktif → Menunggu → Selesai (keputusan A7). Tombol HANYA dari
 *  flag `hak` yang dihitung backend dengan penjaga yang sama dengan endpoint
 *  (`cashback_mesin.hak_program`) — layar tidak menebak siapa PIC/SPV, jadi
 *  tidak ada tombol yang ujungnya 403 (aturan #3). */
function StatusSelesaiBar({ program, hak, aj, snap, busy, sayaPengaju, labelNominal, onAksi }) {
  const st = program.status;
  if (st === ST_PROGRAM_MENUNGGU) {
    return (
      <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-3 flex-wrap" data-testid="cb-prog-status-bar">
        <div className="text-sm text-amber-900">
          <p className="font-semibold inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> Menunggu persetujuan Selesai</p>
          <p className="text-xs mt-0.5">
            Diajukan {aj.oleh_nama || "-"}{aj.at ? ` · ${formatTanggal(aj.at, { denganJam: true })}` : ""} · penyetuju: {program.pic_nama || "PIC"} atau Finance SPV/CEO
            {aj.ringkasan ? ` · total saat diajukan ${idr(aj.ringkasan.total_eligible)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hak.boleh_setujui_selesai && (
            <ActionBtn icon={CheckCircle2} label="Setujui Selesai" onClick={() => onAksi("setujui")} testid="cb-prog-act-setujui-selesai" primary disabled={busy} />
          )}
          {hak.boleh_tolak_selesai && (
            <ActionBtn icon={Undo2} label={sayaPengaju && !hak.boleh_setujui_selesai ? "Tarik Pengajuan" : "Tolak"} onClick={() => onAksi("tolak")}
                       testid="cb-prog-act-tolak-selesai" tone="rose" disabled={busy} />
          )}
          {!hak.boleh_setujui_selesai && sayaPengaju && (
            <span className="text-[11px] text-amber-800">Pengaju tidak bisa menyetujui pengajuannya sendiri.</span>
          )}
        </div>
      </div>
    );
  }
  if (st === ST_PROGRAM_SELESAI) {
    return (
      <div className="px-5 py-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap" data-testid="cb-prog-status-bar">
        <div className="text-sm text-slate-800">
          <p className="font-semibold inline-flex items-center gap-1.5"><Lock className="w-4 h-4" /> Selesai — terkunci</p>
          <p className="text-xs mt-0.5 text-slate-600">
            {snap
              ? `Total ${labelNominal} Eligible dikunci ${idr(snap.total_eligible)} (${snap.jumlah_eligible} unit) · disetujui ${snap.disetujui_oleh_nama || "-"}${snap.at ? ` · ${formatTanggal(snap.at, { denganJam: true })}` : ""}`
              : "Ditutup sebelum 22 Sep 2026 (dibaca Selesai) — tidak punya snapshot; angkanya dihitung dari data terkini."}
          </p>
        </div>
        {hak.boleh_buka_kembali && (
          <ActionBtn icon={LockOpen} label="Buka Kembali" onClick={() => onAksi("buka")} testid="cb-prog-act-buka-kembali" disabled={busy} />
        )}
      </div>
    );
  }
  return (
    <div className="px-5 py-3 bg-emerald-50/60 border-b border-emerald-100 flex items-center justify-between gap-3 flex-wrap" data-testid="cb-prog-status-bar">
      <p className="text-xs text-emerald-900">
        Program berjalan — {program.program_lama ? "unit dihitung ulang dari data terkini." : "unit terjual yang cocok otomatis masuk ke program paling awal."}
        {" "}Selesai perlu persetujuan {program.pic_nama ? `${program.pic_nama} (PIC)` : "PIC"} atau Finance SPV/CEO.
      </p>
      {/* data-testid lama `cb-prog-act-tutup` dipertahankan: tombol "Tutup
          Program" digantikan pengajuan Selesai (`/tutup` di backend pun kini
          berarti mengajukan Selesai). */}
      {hak.boleh_ajukan_selesai && (
        <ActionBtn icon={Lock} label="Ajukan Selesai" onClick={() => onAksi("ajukan")} testid="cb-prog-act-tutup" tone="amber" disabled={busy} />
      )}
    </div>
  );
}

function InfoRow({ label, value, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

// ============ Unit program & eligibility (brief butir 4-10; brief 22 Sep A.1/A.2) ============
const STATUS_TABS = [
  { key: "", label: "Semua" },
  { key: "eligible", label: "Eligible" },
  { key: "pending_activation", label: "Menunggu Aktivasi" },
  { key: "not_eligible", label: "Tidak Eligible" },
  { key: "already_claimed", label: "Sudah Diklaim" },
];

function DoInvoice({ k }) {
  const nomor = k.no_do_supplier || k.po_code;
  return (
    <>
      <p className="text-xs text-slate-800">{formatTanggal(k.tanggal_masuk)}</p>
      <p className="text-[10px] text-slate-500 font-mono">{nomor || "-"}</p>
    </>
  );
}

/** Kolom tabel unit PERSIS brief:
 *  - Aktivasi : IMEI, SN, Produk, Supplier, DO/Invoice = Tanggal Masuk, Tanggal
 *    Terjual, Nama Cabang (toko sendiri) / Nama Pelanggan B2B (dealer), Tanggal
 *    Aktivasi, Nominal Support Aktivasi, Status, Alasan. Modal & Harga Baru DIHAPUS.
 *  - Cashback : IMEI, SN, Unit/Produk, Supplier, DO/Invoice, Tanggal Terjual,
 *    [Nama Pelanggan B2B — dealer], STP PROMO (= Harga Lama), Harga Baru,
 *    Nominal Cashback, Tanggal Aktivasi, Status, Alasan.
 *  - Program LAMA: kolom lamanya; Modal Asli hanya kalau backend mengirimnya
 *    (akses modal, E28). */
function kolomUnit({ mode, dealer, tampilModal }) {
  const k = (id, label, sort, testid, cell, align) => ({ id, label, sort, testid, cell, align });
  const imei = k("imei", "IMEI", "imei", "cb-kandidat-sort-imei",
    (r) => <td className="px-2 py-2 font-mono text-xs">{r.identifier_type === "sn" ? "-" : r.imei}</td>);
  const sn = k("sn", "SN", "sn", "cb-kandidat-sort-sn",
    (r) => <td className="px-2 py-2 font-mono text-xs">{r.serial_number || "-"}</td>);
  // Lebar minimum: tabel ini punya 11-13 kolom, dan tanpa batas bawah nama
  // produk/supplier dipatah per kata sampai satu baris setinggi lima baris.
  const produk = k("produk", mode === "cashback" ? "Unit / Produk" : "Produk", "produk", "cb-kandidat-sort-produk", (r) => (
    <td className="px-2 py-2 min-w-[180px]"><p className="text-slate-900">{r.nama_produk}</p><p className="text-[10px] text-slate-500 font-mono">{r.sku}</p></td>
  ));
  const supplier = k("supplier", "Supplier", "supplier", "cb-kandidat-sort-supplier", (r) => <td className="px-2 py-2 text-xs min-w-[140px]">{r.supplier_nama}</td>);
  const doInv = k("tgl_masuk", "DO / Invoice (Tgl Masuk)", "tgl_masuk", "cb-kandidat-sort-tglmasuk", (r) => <td className="px-2 py-2"><DoInvoice k={r} /></td>);
  const tglJual = k("tgl_jual", "Tanggal Terjual", "tgl_jual", "cb-kandidat-sort-tgljual",
    (r) => <td className="px-2 py-2 text-xs">{r.tanggal_terjual ? formatTanggal(r.tanggal_terjual) : "-"}</td>);
  const pelanggan = k("pihak", "Nama Pelanggan (B2B)", "pihak", "cb-kandidat-sort-pihak", (r) => <td className="px-2 py-2 text-xs">{r.nama_pelanggan || "-"}</td>);
  const cabang = k("pihak", "Nama Cabang", "pihak", "cb-kandidat-sort-pihak", (r) => <td className="px-2 py-2 text-xs">{r.nama_cabang || "-"}</td>);
  const tglAkt = k("tgl_aktivasi", "Tanggal Aktivasi", "tgl_aktivasi", "cb-kandidat-sort-tglaktivasi",
    (r) => <td className="px-2 py-2 text-xs">{r.tanggal_aktivasi ? formatTanggal(r.tanggal_aktivasi) : "-"}</td>);
  const nominal = (label) => k("nominal", label, "cashback", "cb-kandidat-sort-cashback",
    (r) => <td className="px-2 py-2 text-right font-mono font-semibold text-emerald-700">{idr(r.cashback_nominal)}</td>, "right");
  const stp = k("stp", "STP Promo", "stp", "cb-kandidat-sort-stp", (r) => <td className="px-2 py-2 text-right font-mono">{idr(r.harga_lama)}</td>, "right");
  const hargaBaru = k("harga_baru", "Harga Baru", "harga_baru", "cb-kandidat-sort-hargabaru", (r) => <td className="px-2 py-2 text-right font-mono">{idr(r.harga_baru)}</td>, "right");
  const status = k("status", "Status", "status", "cb-kandidat-sort-status", (r) => <td className="px-2 py-2 text-center"><EligibilityPill status={r.status} /></td>);
  const alasan = k("alasan", "Alasan", null, null, (r) => (
    <td className="px-2 py-2 text-[11px] text-slate-600 min-w-[220px]">
      {r.alasan}
      {r.saran_program && (
        <p className="text-sky-700 mt-0.5" data-testid={`cb-kandidat-saran-${r.unit_id}`}>
          Saran: cocok ke {r.saran_program.code} (Program Periode {r.saran_program.label_periode})
        </p>
      )}
      {r.penempatan_sumber === "manual" && <p className="text-violet-700 mt-0.5">Dipindah manual — terkunci di program ini.</p>}
    </td>
  ));
  if (mode === "aktivasi") {
    return [imei, sn, produk, supplier, doInv, tglJual, dealer ? pelanggan : cabang, tglAkt, nominal("Nominal Support Aktivasi"), status, alasan];
  }
  if (mode === "cashback") {
    return [imei, sn, produk, supplier, doInv, tglJual, ...(dealer ? [pelanggan] : []), stp, hargaBaru, nominal("Nominal Cashback"), tglAkt, status, alasan];
  }
  const tglBeli = k("tgl_masuk", "Tgl Beli", "tgl_masuk", "cb-kandidat-sort-tglmasuk", (r) => <td className="px-2 py-2 text-xs">{formatTanggal(r.tanggal_masuk)}</td>);
  const invoice = k("invoice", "Invoice/DO", null, null, (r) => <td className="px-2 py-2 text-center text-slate-400 text-xs">{r.no_do_supplier || "-"}</td>);
  const modal = k("modal", "Modal Asli", "modal", "cb-kandidat-sort-modal", (r) => <td className="px-2 py-2 text-right font-mono">{idr(r.harga_perolehan)}</td>, "right");
  return [imei, produk, supplier, tglBeli, invoice, ...(tampilModal ? [modal] : []), hargaBaru, nominal("Potensi Cashback"), tglAkt, status, alasan];
}

export function KandidatManager({ program, navigate, onClaimCreated, onUnitBerubah }) {
  const { can } = useAuth();
  const hak = program.hak || {};
  const lama = !!program.program_lama;
  const aktivasi = !lama && program.jenis_program === JENIS_AKTIVASI;
  const dealer = !lama && program.kanal === KANAL_DEALER;
  const mode = lama ? "lama" : aktivasi ? "aktivasi" : "cashback";
  // Gerbang tombol = gerbang endpoint-nya (aturan #3): Buat Claim Batch →
  // `POST /cashback/claims` (create); impor/pindah → flag `hak` dari backend
  // (update + program Aktif + bukan program lama untuk pindah).
  const bisaKlaim = can("finance_cashback_claim", "create");
  const bisaPindah = !!hak.boleh_pindah;
  const bisaImpor = !!hak.boleh_impor_aktivasi;
  const labelNominal = aktivasi ? "Support Aktivasi" : "Cashback";

  const [kandidat, setKandidat] = useState([]);
  const [ringkasan, setRingkasan] = useState({});
  const [total, setTotal] = useState({ nominal: 0, unit: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [statusF, setStatusF] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [importResult, setImportResult] = useState(null);
  const [busyImport, setBusyImport] = useState(false);
  const [busyUnduh, setBusyUnduh] = useState(false);
  const [showBuatClaim, setShowBuatClaim] = useState(false);
  const [pindahRows, setPindahRows] = useState(null);
  const [konfirmasiSaran, setKonfirmasiSaran] = useState(false);
  const [reload, setReload] = useState(0);
  const muatUlang = () => setReload((n) => n + 1);

  useEffect(() => {
    let batal = false;
    setLoading(true); setLoadError(null);
    api.get(`${CB_BASE}/programs/${program.id}/kandidat`).then((r) => {
      if (batal) return;
      setKandidat(r.data.items || []);
      setRingkasan(r.data.ringkasan || {});
      setTotal({ nominal: r.data.total_eligible || 0, unit: r.data.jumlah_eligible || 0 });
    }).catch((e) => {
      if (!batal) { setLoadError(e); toast.error(e?.response?.data?.detail || "Gagal memuat daftar unit program"); }
    }).finally(() => { if (!batal) setLoading(false); });
    return () => { batal = true; };
  }, [program.id, program.status, reload]);

  const tabs = mode === "cashback" ? [...STATUS_TABS, { key: ELIG_BELUM_TERJUAL, label: "Belum Terjual" }] : STATUS_TABS;
  const filteredRaw = useMemo(() => kandidat.filter((k) => !statusF || k.status === statusF), [kandidat, statusF]);
  // Eligibility dihitung backend untuk program ini saja (bukan query
  // ber-`to_list(N)` yang bisa terpotong) — aman disortir tanpa
  // BatasDaftarNotice. Pilihan mengacu ke `unit_id`, jadi urutan tampilan
  // tidak memengaruhi unit yang terpilih.
  const kandidatSort = useSortState(null);
  const filtered = sortRows(filteredRaw, kandidatSort.sortKey, kandidatSort.sortDir, {
    imei: (k) => k.imei || "",
    sn: (k) => k.serial_number || "",
    produk: (k) => k.nama_produk || "",
    supplier: (k) => k.supplier_nama || "",
    tgl_masuk: (k) => k.tanggal_masuk || "",
    tgl_jual: (k) => k.tanggal_terjual || "",
    pihak: (k) => (dealer ? k.nama_pelanggan : k.nama_cabang) || "",
    modal: (k) => k.harga_perolehan || 0,
    stp: (k) => k.harga_lama || 0,
    harga_baru: (k) => k.harga_baru || 0,
    cashback: (k) => k.cashback_nominal || 0,
    tgl_aktivasi: (k) => k.tanggal_aktivasi || "",
    status: (k) => k.status || "",
  });
  const tampilModal = kandidat.some((k) => k.harga_perolehan !== undefined && k.harga_perolehan !== null);
  const kolom = kolomUnit({ mode, dealer, tampilModal });

  const bolehPilih = (k) => (k.status === "eligible" && bisaKlaim) || (ELIG_BOLEH_PINDAH.includes(k.status) && bisaPindah);
  const adaPilihan = bisaKlaim || bisaPindah;
  const selectedItems = useMemo(() => kandidat.filter((k) => selected.has(k.unit_id)), [kandidat, selected]);
  const totalSelected = useMemo(() => round2(selectedItems.reduce((a, b) => a + (b.cashback_nominal || 0), 0)), [selectedItems]);
  const semuaEligible = selectedItems.length > 0 && selectedItems.every((k) => k.status === "eligible");
  const semuaBolehPindah = selectedItems.length > 0 && selectedItems.every((k) => ELIG_BOLEH_PINDAH.includes(k.status));

  const toggle = (k) => {
    if (!bolehPilih(k)) return;
    setSelected((prev) => { const n = new Set(prev); n.has(k.unit_id) ? n.delete(k.unit_id) : n.add(k.unit_id); return n; });
  };
  const dapatDipilih = filtered.filter(bolehPilih);
  const semuaTerpilih = dapatDipilih.length > 0 && dapatDipilih.every((k) => selected.has(k.unit_id));
  const toggleSemua = () => setSelected((prev) => {
    const n = new Set(prev);
    dapatDipilih.forEach((k) => (semuaTerpilih ? n.delete(k.unit_id) : n.add(k.unit_id)));
    return n;
  });

  const unduh = async (url, nama) => {
    setBusyUnduh(true);
    try { await downloadBlob(url, nama, MIME_XLSX); }
    catch { toast.error("Gagal mengunduh berkas Excel"); }
    finally { setBusyUnduh(false); }
  };
  // Program ber-jenis: template TERISI unit terjual program ini (brief A.1 —
  // staf cukup mengisi tanggal). Program lama: template kosong 2 kolom.
  const unduhTemplate = () => (lama
    ? unduh(`${CB_BASE}/programs/import-template`, "template-aktivasi-imei.xlsx")
    : unduh(`${CB_BASE}/programs/${program.id}/template-aktivasi`, `template-aktivasi-${program.code}.xlsx`));
  const unduhRekap = () => unduh(`${CB_BASE}/programs/${program.id}/rekap-xlsx`, `rekap-${program.code}.xlsx`);

  const importFile = async (file) => {
    if (!file) return;
    setBusyImport(true); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`${CB_BASE}/programs/${program.id}/import-aktivasi`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      setImportResult(r.data);
      setSelected(new Set());
      muatUlang();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengimpor tanggal aktivasi"); }
    finally { setBusyImport(false); }
  };

  // A13: saran dihitung backend per unit (`saran_program`); tombol hanya
  // meneruskan unit_id — tujuan dihitung ULANG di server supaya saran basi
  // tidak ikut dieksekusi.
  const barisSaran = useMemo(() => kandidat.filter((k) => k.saran_program), [kandidat]);
  const grupSaran = useMemo(() => {
    const g = {};
    barisSaran.forEach((k) => {
      const s = k.saran_program;
      g[s.id] = g[s.id] || { ...s, jumlah: 0 };
      g[s.id].jumlah += 1;
    });
    return Object.values(g);
  }, [barisSaran]);

  const setelahPindah = () => { setSelected(new Set()); setPindahRows(null); setKonfirmasiSaran(false); muatUlang(); onUnitBerubah?.(); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="cb-kandidat-total">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Total {labelNominal} (Eligible)</p>
          <p className="font-display text-2xl font-bold text-emerald-800 mt-0.5">{idr(total.nominal)}</p>
          <p className="text-[11px] text-emerald-800/80 mt-0.5">
            {total.unit} unit eligible, termasuk yang sudah diklaim
            {kandidat.some((k) => k.dari_snapshot) ? " · angka terkunci (snapshot Selesai)" : ""}
          </p>
        </div>

        <div className="lg:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Tanggal Aktivasi & Rekap (Excel)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={unduhTemplate} disabled={busyUnduh} data-testid="cb-import-template"
                      className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50">
                <FileDown className="w-3.5 h-3.5" /> Unduh Template
              </button>
              {bisaImpor && (
                <label className="h-8 px-3 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> {busyImport ? "Mengimpor..." : "Impor .xlsx"}
                  <input type="file" accept=".xlsx" className="hidden" data-testid="cb-import-file-input" disabled={busyImport}
                         onChange={(e) => { importFile(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
              <button onClick={unduhRekap} disabled={busyUnduh} data-testid="cb-rekap-xlsx"
                      className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" /> Download Rekapan Excel
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            {lama
              ? "Template: IMEI | Tanggal Aktivasi (YYYY-MM-DD)."
              : "Template berisi IMEI, Serial Number, dan Type Produk unit yang sudah terjual di program ini — cukup isi kolom Tanggal Aktivasi lalu impor; nominal dihitung otomatis dari periode program. Rekap dipisah per lembar: Eligible, Tidak Eligible, Menunggu Aktivasi, Sudah Diklaim."}
            {!bisaImpor && program.status !== ST_PROGRAM_AKTIF ? " Impor terkunci karena program tidak Aktif." : ""}
          </p>
          {importResult && <HasilImporAktivasi hasil={importResult} onTutup={() => setImportResult(null)} />}
        </div>
      </div>

      {grupSaran.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 flex items-start justify-between gap-3 flex-wrap" data-testid="cb-saran-banner">
          <div className="text-xs text-sky-900 space-y-0.5">
            <p className="font-semibold">{barisSaran.length} unit Tidak Eligible di sini cocok ke program lain menurut tanggal aktivasinya:</p>
            {grupSaran.map((g) => (
              <p key={g.id}>• {g.jumlah} unit → {g.code} · Program Periode {g.label_periode}</p>
            ))}
          </div>
          {bisaPindah && (
            <button onClick={() => setKonfirmasiSaran(true)} data-testid="cb-saran-pindah-semua"
                    className="h-8 px-3 rounded-lg bg-sky-700 hover:bg-sky-800 text-white text-xs font-medium inline-flex items-center gap-1.5">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Pindahkan semua sesuai saran
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-1.5 flex-wrap">
        {tabs.map((t) => {
          const r = t.key ? ringkasan[t.key] : null;
          return (
            <button key={t.key || "all"} onClick={() => setStatusF(t.key)} data-testid={`cb-kandidat-filter-${t.key || "semua"}`}
                    className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 ${statusF === t.key ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {t.label}{r ? ` (${r.count} · ${idr(r.total_cashback)})` : ""}
            </button>
          );
        })}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-x-auto max-h-[420px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 sticky top-0 z-[1]">
            <tr>
              {adaPilihan && (
                <th className="px-2 py-2 text-center">
                  <input type="checkbox" data-testid="cb-kandidat-select-all" onChange={toggleSemua}
                         checked={semuaTerpilih} disabled={dapatDipilih.length === 0}
                         title="Pilih semua unit yang bisa diproses pada filter ini" />
                </th>
              )}
              {kolom.map((c) => (c.sort ? (
                <SortTh key={c.id} label={c.label} sortKeyName={c.sort} sortKey={kandidatSort.sortKey} sortDir={kandidatSort.sortDir}
                        onSort={kandidatSort.toggle} align={c.align} testid={c.testid} />
              ) : (
                <th key={c.id} className="px-2 py-2 text-left">{c.label}</th>
              )))}
              {bisaPindah && <th className="px-2 py-2 text-right">Aksi</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            <LoadingState loading={loading} error={loadError} onRetry={muatUlang} kosong={!loading && !loadError && filtered.length === 0}
                          pesanKosong={lama ? "Tidak ada IMEI kandidat pada filter ini" : "Belum ada unit terjual yang masuk program ini pada filter ini"}
                          varian="tabel" kolom={kolom.length + (adaPilihan ? 1 : 0) + (bisaPindah ? 1 : 0)} data-testid="cb-kandidat-loading">
              {filtered.map((k) => {
                const bisa = bolehPilih(k);
                const bisaPindahBaris = bisaPindah && ELIG_BOLEH_PINDAH.includes(k.status);
                return (
                  <tr key={k.unit_id || k.imei} data-testid={`cb-kandidat-row-${k.imei}`} className={selected.has(k.unit_id) ? "bg-emerald-50/50" : ""}>
                    {adaPilihan && (
                      <td className="px-2 py-2 text-center">
                        <input type="checkbox" data-testid={`cb-kandidat-cb-${k.imei}`} checked={selected.has(k.unit_id)}
                               disabled={!bisa} onChange={() => toggle(k)}
                               title={!bisa ? "Hanya unit Eligible (untuk Claim Batch) atau Tidak Eligible / Menunggu Aktivasi (untuk dipindah) yang bisa dipilih" : undefined} />
                      </td>
                    )}
                    {kolom.map((c) => <Fragment key={c.id}>{c.cell(k)}</Fragment>)}
                    {bisaPindah && (
                      <td className="px-2 py-2 text-right">
                        {bisaPindahBaris && (
                          <button onClick={() => setPindahRows([k])} data-testid={`cb-kandidat-pindah-${k.unit_id}`}
                                  className="h-7 px-2 rounded-md border border-slate-200 text-[11px] font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1 whitespace-nowrap">
                            <ArrowRightLeft className="w-3 h-3" /> Pindahkan
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </LoadingState>
          </tbody>
        </table>
      </div>

      {adaPilihan && (
        <div className="flex items-center justify-between flex-wrap gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="cb-kandidat-aksi">
          <div className="text-sm text-emerald-800">
            <p><span className="font-semibold">{selected.size} unit</span> terpilih · Nominal <span className="font-semibold">{idr(totalSelected)}</span></p>
            {selected.size > 0 && !semuaEligible && !semuaBolehPindah && (
              <p className="text-[11px] text-amber-800 mt-0.5">Pilihan bercampur: Claim Batch hanya untuk unit Eligible; Pindahkan hanya untuk unit Tidak Eligible / Menunggu Aktivasi.</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {bisaPindah && (
              <button onClick={() => setPindahRows(selectedItems)} disabled={!semuaBolehPindah} data-testid="cb-pindah-btn"
                      className="h-9 px-4 rounded-lg border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                <ArrowRightLeft className="w-4 h-4" /> Pindahkan
              </button>
            )}
            {bisaKlaim && (
              <button onClick={() => setShowBuatClaim(true)} disabled={!semuaEligible} data-testid="cb-buat-claim-btn"
                      className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                <PlusCircle className="w-4 h-4" /> Buat Claim Batch
              </button>
            )}
          </div>
        </div>
      )}

      {showBuatClaim && (
        <BuatClaimModal program={program} selectedItems={selectedItems} totalSelected={totalSelected} labelNominal={labelNominal}
                        onClose={() => setShowBuatClaim(false)}
                        onCreated={(batch) => {
                          setShowBuatClaim(false); setSelected(new Set());
                          // Permintaan bos 21 Agu E1: klaim di luar periode klaim TETAP tersimpan
                          // — toast MERAH (bukan gagal) supaya staf langsung sadar sebelum lanjut
                          // ke halaman detail (yang juga menampilkan panel & pill peringatan sama).
                          if (batch.di_luar_periode_klaim) {
                            toast.error(`Claim Batch ${batch.code} dibuat, TAPI di luar periode klaim — ${batch.di_luar_periode_klaim_alasan || ""}`);
                          } else {
                            toast.success(`Claim Batch ${batch.code} dibuat ✓`);
                          }
                          onClaimCreated?.(); navigate?.(`/finance/cashback/claims/${batch.id}`);
                        }} />
      )}
      {pindahRows && (
        <PindahUnitModal program={program} rows={pindahRows} onClose={() => setPindahRows(null)} onDone={setelahPindah} />
      )}
      {konfirmasiSaran && (
        <PindahSaranModal program={program} rows={barisSaran} grup={grupSaran} onClose={() => setKonfirmasiSaran(false)} onDone={setelahPindah} />
      )}
    </div>
  );
}

function HasilImporAktivasi({ hasil, onTutup }) {
  const gagal = hasil.gagal || [];
  const konflik = hasil.konflik_sn || [];
  const peringatan = hasil.peringatan || [];
  const diLuar = hasil.di_luar_program || [];
  return (
    <div data-testid="cb-import-result" className="text-xs bg-white border border-slate-200 rounded-lg p-2.5 space-y-2">
      <p className="text-slate-700">
        <b>{hasil.diperbarui}</b> tanggal aktivasi diperbarui · {hasil.tidak_berubah ?? 0} tidak berubah
        {hasil.dilewati ? ` · ${hasil.dilewati} baris tanpa tanggal dilewati` : ""}
        {hasil.sn_dilengkapi ? ` · ${hasil.sn_dilengkapi} Serial Number dilengkapi` : ""}
      </p>
      {(hasil.saran || []).length > 0 && (
        <p className="text-sky-800">{hasil.saran.length} unit cocok ke program lain — lihat saran di atas tabel.</p>
      )}
      {gagal.length > 0 && (
        <div>
          <p className="text-rose-700 font-medium">{hasil.jumlah_gagal ?? gagal.length} baris gagal:</p>
          <div className="max-h-32 overflow-y-auto mt-1">
            <table className="w-full text-[11px]">
              <thead className="text-slate-500"><tr><th className="text-left pr-2">Baris</th><th className="text-left">Alasan</th></tr></thead>
              <tbody>
                {gagal.map((g, i) => (
                  <tr key={i} className="border-t border-slate-100"><td className="pr-2 py-0.5 font-mono">{g.baris}</td><td className="py-0.5 text-slate-600">{g.imei ? `${g.imei} — ` : ""}{g.alasan}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {konflik.length > 0 && (
        <div data-testid="cb-import-konflik-sn">
          <p className="text-amber-800 font-medium">{konflik.length} baris Serial Number-nya tidak cocok dengan unit — SN TIDAK ditimpa dan tanggal aktivasinya TIDAK diimpor. Periksa file, lalu impor ulang:</p>
          <div className="max-h-28 overflow-y-auto mt-1 space-y-0.5">
            {konflik.map((c, i) => (
              <p key={i} className="text-slate-600">Baris {c.baris} · {c.imei}: file {c.sn_file}{c.sn_tercatat ? `, tercatat ${c.sn_tercatat}` : ""}{c.alasan ? ` — ${c.alasan}` : ""}</p>
            ))}
          </div>
        </div>
      )}
      {peringatan.length > 0 && (
        <div>
          <p className="text-amber-800 font-medium">Perhatian:</p>
          {peringatan.map((p, i) => <p key={i} className="text-slate-600">• {p}</p>)}
        </div>
      )}
      {diLuar.length > 0 && (
        <p className="text-slate-600">{diLuar.length} IMEI diperbarui tanggalnya tapi bukan unit program ini: {diLuar.slice(0, 10).join(", ")}{diLuar.length > 10 ? ", ..." : ""}</p>
      )}
      <button onClick={onTutup} className="text-slate-400 hover:text-slate-700 text-[11px]">Tutup laporan</button>
    </div>
  );
}

/** Pindahkan unit ke program SEJENIS lain lewat dropdown bertingkat (brief A.1/A.2):
 *  pertama kanal ("Support Aktivasi <toko>" / "... Dealer Partner"), lalu
 *  "Program Periode d-d Bulan YYYY · supplier". Isinya dari `GET /cashback/
 *  programs/opsi-pindah?program_asal=` (hanya program Aktif sejenis); backend
 *  menyertakan `unit_tidak_cocok` per program dengan aturan yang SAMA dengan
 *  penjaga pindah (supplier, batas tanggal masuk, SKU). Program yang pasti
 *  menolak salah satu unit terpilih tetap tampil tapi tidak bisa dipilih,
 *  beserta alasannya — bukan tombol yang ujungnya 400. */
function PindahUnitModal({ program, rows, onClose, onDone }) {
  const [grup, setGrup] = useState(null);
  const [kanalPilih, setKanalPilih] = useState(program.kanal || KANAL_TOKO);
  const [tujuan, setTujuan] = useState("");
  const [catatan, setCatatan] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let batal = false;
    api.get(`${CB_BASE}/programs/opsi-pindah`, { params: { jenis: program.jenis_program, program_asal: program.id } })
      .then((r) => { if (!batal) setGrup(r.data.grup || []); })
      .catch((e) => { if (!batal) { setGrup([]); toast.error(e?.response?.data?.detail || "Gagal memuat daftar program tujuan"); } });
    return () => { batal = true; };
  }, [program.jenis_program, program.id]);

  const skuDibutuhkan = useMemo(() => [...new Set(rows.map((r) => r.sku))], [rows]);
  const grupKanal = (grup || []).find((g) => g.kanal === kanalPilih);
  const pilihan = (grupKanal?.programs || []).filter((p) => p.id !== program.id).map((p) => {
    // Alasan dari server (aturan penjaga pindah); cek SKU hanya cadangan kalau
    // server belum mengirim `unit_tidak_cocok`.
    const alasan = p.unit_tidak_cocok
      ? rows.map((r) => p.unit_tidak_cocok[r.unit_id]).filter(Boolean)
      : (skuDibutuhkan.every((s) => (p.skus || []).includes(s)) ? [] : ["SKU tidak terdaftar"]);
    return { ...p, cocok: alasan.length === 0, alasan };
  });
  const pindahKanal = kanalPilih !== program.kanal;

  const submit = async () => {
    if (!tujuan) return;
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/programs/${program.id}/pindah-unit`, {
        program_tujuan_id: tujuan, unit_ids: rows.map((k) => k.unit_id), catatan,
      });
      toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindahkan unit"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Pindahkan ${rows.length} Unit`} icon={ArrowRightLeft} onClose={onClose} onSubmit={submit}
           submitLabel="Pindahkan" busy={busy} disabled={!tujuan} testid="cb-pindah-modal">
      <p className="text-xs text-slate-500">
        Unit langsung keluar dari {program.code} dan masuk program tujuan. Pilihan manual MENGUNCI unit di program tujuan — tidak dipindah otomatis lagi walau kanal notanya berubah.
      </p>
      <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
        {rows.map((k) => (
          <div key={k.unit_id} className="px-3 py-1.5 flex items-center justify-between gap-2 text-xs">
            <span className="font-mono">{k.imei}</span>
            <span className="text-slate-500 truncate">{k.nama_produk}</span>
            <EligibilityPill status={k.status} />
          </div>
        ))}
      </div>
      {grup === null ? (
        <p className="text-xs text-slate-400">Memuat program tujuan...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Pindahkan ke">
            <select value={kanalPilih} onChange={(e) => { setKanalPilih(e.target.value); setTujuan(""); }} data-testid="cb-pindah-kanal" className={inputCls}>
              {(grup || []).map((g) => <option key={g.kanal} value={g.kanal}>{g.label}</option>)}
            </select>
          </Field>
          <Field label="Program Periode" hint={pilihan.length === 0 ? "Belum ada program Aktif lain di kanal ini." : undefined}>
            <select value={tujuan} onChange={(e) => setTujuan(e.target.value)} data-testid="cb-pindah-program" className={inputCls}>
              <option value="">Pilih program...</option>
              {pilihan.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.cocok}>
                  {p.label} · {p.code} · {p.supplier_nama}{p.cocok ? "" : ` — tidak bisa: ${p.alasan[0]}${p.alasan.length > 1 ? ` (+${p.alasan.length - 1} unit lain)` : ""}`}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
      {pindahKanal && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
          Pindah kanal — dipakai misalnya saat unit yang diretur Dealer Partner dijual lagi oleh toko sendiri (atau sebaliknya). Pastikan penjualannya memang berpindah kanal.
        </div>
      )}
      <Field label="Catatan (opsional)">
        <input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="cb-pindah-catatan" className={inputCls} />
      </Field>
    </Modal>
  );
}

function PindahSaranModal({ program, rows, grup, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/programs/${program.id}/pindah-sesuai-saran`, { unit_ids: rows.map((k) => k.unit_id) });
      toast.success(r.data.message);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindahkan sesuai saran"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Pindahkan Semua Sesuai Saran" icon={ArrowRightLeft} onClose={onClose} onSubmit={submit}
           submitLabel={`Pindahkan ${rows.length} Unit`} busy={busy} testid="cb-saran-modal">
      <p className="text-sm text-slate-600">Unit Tidak Eligible berikut dipindah ke program yang periodenya mencakup tanggal aktivasinya:</p>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 text-xs">
        {grup.map((g) => (
          <p key={g.id} className="px-3 py-2">{g.jumlah} unit → <b>{g.code}</b> · Program Periode {g.label_periode}</p>
        ))}
      </div>
      <p className="text-[11px] text-slate-500">Tujuan dihitung ulang oleh server saat ditekan — saran yang sudah tidak berlaku dilewati dan dilaporkan.</p>
    </Modal>
  );
}

function BuatClaimModal({ program, selectedItems, totalSelected, labelNominal, onClose, onCreated }) {
  const [keterangan, setKeterangan] = useState("");
  const [busy, setBusy] = useState(false);
  // Peringatan PRA-SIMPAN (murni perbandingan tanggal di klien, sama seperti
  // `lewatDeadline` di SubmitModal) — klaim ini akan dibuat HARI INI, jadi
  // dibandingkan langsung terhadap periode_klaim_dari/sampai program. Backend
  // TETAP jadi sumber kebenaran (`_cek_periode_klaim`); ini cuma bantu staf
  // sadar SEBELUM menekan submit, bukan pengganti validasi server.
  const hariIni = hariIniISO();
  const diLuarPeriodeKlaim = !!(
    (program.periode_klaim_dari && hariIni < program.periode_klaim_dari) ||
    (program.periode_klaim_sampai && hariIni > program.periode_klaim_sampai)
  );
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`${CB_BASE}/claims`, { program_id: program.id, imeis: selectedItems.map((k) => k.imei), keterangan });
      onCreated(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat Claim Batch"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Buat Claim Batch" icon={PlusCircle} onClose={onClose} onSubmit={submit} submitLabel="Buat Claim Batch"
           busy={busy} testid="cb-buat-claim-modal">
      <p className="text-sm text-slate-600">{selectedItems.length} IMEI · Total {labelNominal} <b>{idr(totalSelected)}</b></p>
      <p className="text-xs text-slate-500">Supplier: {program.supplier_nama}</p>
      {diLuarPeriodeKlaim && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start gap-2" data-testid="cb-buat-claim-warn-periode">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Hari ini ({formatTanggal(hariIni)}) di LUAR periode klaim program
          ({program.periode_klaim_dari ? formatTanggal(program.periode_klaim_dari) : "-"} s.d. {program.periode_klaim_sampai ? formatTanggal(program.periode_klaim_sampai) : "-"})
          — klaim tetap bisa dibuat, tapi supplier berhak menolak karena di luar periode.
        </div>
      )}
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
        {selectedItems.map((k) => (
          <div key={k.unit_id || k.imei} className="px-3 py-1.5 flex items-center justify-between text-xs">
            <span className="font-mono">{k.imei}</span><span className="text-slate-500">{k.sku}</span><span className="font-mono">{idr(k.cashback_nominal)}</span>
          </div>
        ))}
      </div>
      <Field label="Keterangan (opsional)">
        <textarea rows={2} value={keterangan} onChange={(e) => setKeterangan(e.target.value)} data-testid="cb-buat-claim-ket" className={inputCls + " h-auto py-2"} />
      </Field>
    </Modal>
  );
}
